/**
 * ExpensesPage.js - Gestão de Despesas (PGT)
 * Fase B - Módulo de Despesas
 * 
 * Features:
 * - Lançamento de despesa (categoria, subcategoria, conta, localização)
 * - Workflow: Draft → Registada → Paga
 * - Pagamentos múltiplos por despesa
 * - Auditoria completa
 * - Relatórios por período/categoria/conta
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { expensesAPI, catalogsAPI, settingsAPI, procurementAPI, inventoryAPI, getErrorMessage } from '@/lib/api';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ExpensesFiltersDeck } from '@/components/expenses/ExpensesFiltersDeck';
import { ExpensesItemsRollupCard } from '@/components/expenses/ExpensesItemsRollupCard';
import { ExpenseItemsEditor } from '@/components/expenses/ExpenseItemsEditor';
import { ExpenseItemsTable } from '@/components/expenses/ExpenseItemsTable';
import { ExpenseSplitPaymentsEditor } from '@/components/expenses/ExpenseSplitPaymentsEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { TABLE_DENSITY } from '@/lib/tableDensity';
import { 
  Plus, Receipt, AlertCircle, Eye, Edit, Check, X, Send, CreditCard,
  Calendar as CalendarIcon, History, MapPin, Warehouse, Store, 
  FileText, DollarSign, Trash2, RotateCcw, Clock, User, TrendingUp,
  Banknote, Building2, MoreHorizontal, ChevronDown, ChevronUp
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatDate, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';
import ExportButton from '@/components/ExportButton';
import { pt } from 'date-fns/locale';
import { useLanguage } from '@/i18n';
import { applyCleanOrLegacyParam, buildHandleMaps, resolveHandleOrLegacyId } from '@/lib/queryHandles';

const EXPENSE_COLUMNS_STORAGE_KEY = 'expenses-operational-columns-v2';
const readExpenseColumnWidths = () => {
  if (typeof window === 'undefined') return DEFAULT_EXPENSE_COLUMN_WIDTHS;
  try {
    const saved = window.sessionStorage.getItem(EXPENSE_COLUMNS_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : DEFAULT_EXPENSE_COLUMN_WIDTHS;
    return Array.isArray(parsed) && parsed.length === DEFAULT_EXPENSE_COLUMN_WIDTHS.length ? parsed : DEFAULT_EXPENSE_COLUMN_WIDTHS;
  } catch {
    return DEFAULT_EXPENSE_COLUMN_WIDTHS;
  }
};
const writeExpenseColumnWidths = (value) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(EXPENSE_COLUMNS_STORAGE_KEY, JSON.stringify(value));
};

// Status configuration
const STATUS_CONFIG = {
  draft: { label: 'Rascunho', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText },
  registered: { label: 'Registada', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Receipt },
  pending_approval: { label: 'Pendente Aprovação', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  approved: { label: 'Aprovada', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Check },
  paid: { label: 'Paga', color: 'bg-green-100 text-green-700 border-green-200', icon: CreditCard },
  rejected: { label: 'Rejeitada', color: 'bg-red-100 text-red-700 border-red-200', icon: X },
  cancelled: { label: 'Cancelada', color: 'bg-gray-100 text-gray-500 border-gray-200', icon: Trash2 },
};

// Status options for filter
const EXPENSES_STATUS_OPTIONS = ['draft', 'registered', 'paid', 'cancelled', 'pending_approval', 'approved', 'rejected'];

// Status labels for GlobalFilters
const EXPENSES_STATUS_LABELS = {
  draft: 'Rascunho',
  registered: 'Registada',
  cancelled: 'Cancelada',
  pending_approval: 'Pendente',
  approved: 'Aprovada',
  paid: 'Paga',
  rejected: 'Rejeitada',
};

const EXPENSE_STATUS_TABS = [
  { value: 'all', label: 'Todo período', description: 'Visão geral actual', icon: Receipt },
  { value: 'draft', label: 'Rascunho', description: 'Ainda por fechar', icon: FileText },
  { value: 'registered', label: 'Registadas', description: 'Sem aprovação manual', icon: Receipt },
  { value: 'paid', label: 'Pagas', description: 'Liquidadas com sucesso', icon: CreditCard },
  { value: 'cancelled', label: 'Canceladas', description: 'Prontas para eliminação permanente', icon: Trash2 },
];

const EXPENSE_PERIOD_LABELS = {
  all: 'Todo período',
  today: 'Hoje',
  yesterday: 'Ontem',
  this_week: 'Esta semana',
  this_month: 'Este mês',
  last_month: 'Mês passado',
  this_year: 'Este ano',
  last_year: 'Ano passado',
  custom: 'Período personalizado',
};

const PGT01_ROOT_CODE = 'PGT01';

const INVENTORY_SYNC_STATUS_LABELS = {
  not_applicable: 'Fora do fluxo',
  not_linked: 'Ligada ao inventário',
  pending_receipt: 'Pendente de entrada em stock',
  received: 'Recebida / entrada confirmada',
  sync_error: 'Erro de sincronização',
  cancelled: 'Anulada',
  reversal_blocked: 'Reversão bloqueada',
};

const PGT01_OPERATIONAL_STATUS_LABELS = {
  pending_receipt: 'Pendente de recepção',
  received: 'Recepção confirmada',
  cancelled: 'Anulada com reversão',
  reversal_blocked: 'Reversão bloqueada',
  not_linked: 'Ligação criada',
};

const EXPENSE_SORT_OPTIONS = [
  { value: 'expense_date', label: 'Data' },
  { value: 'document', label: 'Documento' },
  { value: 'description', label: 'Descrição' },
  { value: 'pgt', label: 'PGT' },
  { value: 'account_location', label: 'Conta / Loja' },
  { value: 'items', label: 'Artigos' },
  { value: 'amount', label: 'Valor' },
];

const EXPENSE_AUDIT_FIELD_LABELS = {
  description: 'Descrição',
  amount: 'Valor',
  expense_category_id: 'Categoria',
  expense_subcategory_id: 'Subcategoria',
  payment_account_id: 'Conta de pagamento',
  location_id: 'Localização',
  expense_date: 'Data da despesa',
  reference_number: 'Factura / Recibo',
  counterparty_type: 'Tipo de destinatário',
  supplier_id: 'Fornecedor',
  beneficiary_employee_id: 'Beneficiário',
  supplier_name: 'Nome do destinatário',
  notes: 'Notas',
  registered_at: 'Data/hora do registo',
  items: 'Artigos',
  payments: 'Pagamentos',
  payment_balance: 'Saldo',
  payment_reference: 'Referência do pagamento',
  payment_notes: 'Notas do pagamento',
  status: 'Estado',
  expense_category_name: 'Nome da categoria',
  expense_subcategory_name: 'Nome da subcategoria',
  payment_account_name: 'Nome da conta',
  location_name: 'Nome da localização',
};

const formatExpenseAuditChangeValue = (field, value) => {
  if (value === null || value === undefined || value === '' || value === 'None' || value === 'null') {
    return '—';
  }

  if (field === 'items') return 'Artigos actualizados';
  if (field === 'payments') return 'Pagamentos actualizados';

  const text = String(value).replace(/\s+/g, ' ').trim();
  if (text.length > 160) {
    return `${text.slice(0, 157)}…`;
  }
  return text;
};

// Icon mapping for location types
const LOCATION_TYPE_ICONS = {
  warehouse: Warehouse,
  store: Store,
};

const generateExpenseItemId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `expense-item-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
};

const createEmptyExpenseItem = () => ({
  id: generateExpenseItemId(),
  description: '',
  unit: 'un',
  quantity: '1',
  used_quantity: '0',
  unit_price: '',
  remaining_quantity: '1',
  total: '0.00',
  inventory_linked: false,
  inventory_product_id: '',
  inventory_product_name: '',
  inventory_location_id: '',
  inventory_location_name: '',
  inventory_status: 'not_linked',
  inventory_requested_quantity: '0',
  inventory_received_quantity: '0',
  inventory_pending_quantity: '0',
});

const calculateExpenseItemPreview = (item) => {
  const quantity = Number(item.quantity || 0);
  const usedQuantity = Number(item.used_quantity || 0);
  const unitPrice = Number(item.unit_price || 0);
  const remainingQuantity = Math.max(quantity - usedQuantity, 0);
  const inventoryLinked = Boolean(item.inventory_linked);
  const inventoryReceivedQuantity = Number(item.inventory_received_quantity || 0);
  return {
    ...item,
    remaining_quantity: quantity > 0 ? remainingQuantity.toFixed(3).replace(/\.000$/, '') : '0',
    total: (quantity * unitPrice).toFixed(2),
    inventory_requested_quantity: inventoryLinked ? String(quantity || 0) : '0',
    inventory_pending_quantity: inventoryLinked ? String(Math.max(quantity - inventoryReceivedQuantity, 0)) : '0',
    inventory_status: inventoryLinked ? (item.inventory_status || 'pending_receipt') : 'not_linked',
  };
};

const hydrateExpenseItems = (items = []) => items.map((item) => calculateExpenseItemPreview({
  id: item.id || generateExpenseItemId(),
  description: item.description || '',
  unit: item.unit || 'un',
  quantity: item.quantity?.toString?.() || String(item.quantity || 0),
  used_quantity: item.used_quantity?.toString?.() || String(item.used_quantity || 0),
  unit_price: item.unit_price?.toString?.() || String(item.unit_price || 0),
  inventory_linked: Boolean(item.inventory_linked),
  inventory_product_id: item.inventory_product_id || '',
  inventory_product_name: item.inventory_product_name || '',
  inventory_location_id: item.inventory_location_id || '',
  inventory_location_name: item.inventory_location_name || '',
  inventory_status: item.inventory_status || 'not_linked',
  inventory_requested_quantity: item.inventory_requested_quantity?.toString?.() || String(item.inventory_requested_quantity || 0),
  inventory_received_quantity: item.inventory_received_quantity?.toString?.() || String(item.inventory_received_quantity || 0),
  inventory_pending_quantity: item.inventory_pending_quantity?.toString?.() || String(item.inventory_pending_quantity || 0),
}));

const summarizeExpenseItems = (items = []) => {
  return items.reduce((accumulator, item) => {
    accumulator.total += Number(item.total || 0);
    accumulator.quantity += Number(item.quantity || 0);
    accumulator.used += Number(item.used_quantity || 0);
    accumulator.remaining += Number(item.remaining_quantity || 0);
    return accumulator;
  }, { total: 0, quantity: 0, used: 0, remaining: 0 });
};

const validateExpenseItems = (items = []) => {
  const invalidRow = items.find((item) => {
    const hasAnyValue = [item.description, item.unit, item.quantity, item.used_quantity, item.unit_price].some((value) => String(value ?? '').trim() !== '');
    if (!hasAnyValue) return false;
    const quantity = Number(item.quantity || 0);
    const usedQuantity = Number(item.used_quantity || 0);
    const unitPrice = Number(item.unit_price || 0);
    const inventoryInvalid = item.inventory_linked && (!item.inventory_product_id || !item.inventory_location_id || quantity <= 0);
    return !item.description?.trim() || !item.unit?.trim() || quantity <= 0 || unitPrice < 0 || usedQuantity < 0 || usedQuantity > quantity || inventoryInvalid;
  });

  if (!invalidRow) return null;
  return 'Revise os artigos: descrição, unidade, quantidade, aplicada e preço unitário devem estar válidos. Linhas ligadas ao inventário exigem produto e armazém.';
};

const serializeExpenseItems = (items = []) => items.map((item) => ({
  id: item.id,
  description: item.description.trim(),
  unit: item.unit.trim() || 'un',
  quantity: Number(item.quantity || 0),
  used_quantity: Number(item.used_quantity || 0),
  unit_price: Number(item.unit_price || 0),
  inventory_linked: Boolean(item.inventory_linked),
  inventory_product_id: item.inventory_linked ? item.inventory_product_id || null : null,
  inventory_location_id: item.inventory_linked ? item.inventory_location_id || null : null,
  inventory_status: item.inventory_linked ? (item.inventory_status || 'pending_receipt') : 'not_linked',
  inventory_requested_quantity: item.inventory_linked ? Number(item.quantity || 0) : 0,
  inventory_received_quantity: item.inventory_linked ? Number(item.inventory_received_quantity || 0) : 0,
}));

const generateExpensePaymentId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `expense-payment-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
};

const createEmptyExpensePayment = () => ({
  id: generateExpensePaymentId(),
  payment_method_id: '',
  amount: '',
  reference: '',
  notes: '',
});

const hydrateExpensePayments = (payments = []) => (
  (payments || []).length > 0
    ? payments.map((payment) => ({
      id: payment.id || generateExpensePaymentId(),
      payment_method_id: payment.payment_method_id || '',
      amount: payment.amount?.toString?.() || String(payment.amount || ''),
      reference: payment.reference || '',
      notes: payment.notes || '',
    }))
    : [createEmptyExpensePayment()]
);

const serializeExpensePayments = (payments = []) => payments
  .filter((payment) => [payment.payment_method_id, payment.amount, payment.reference, payment.notes]
    .some((value) => String(value ?? '').trim() !== ''))
  .map((payment) => ({
    id: payment.id || generateExpensePaymentId(),
    payment_method_id: payment.payment_method_id,
    amount: Number(payment.amount || 0),
    reference: payment.reference?.trim() || null,
    notes: payment.notes?.trim() || null,
  }));

const summarizeExpensePayments = (payments = []) => payments.reduce((accumulator, payment) => {
  const amount = Number(payment.amount || 0) || 0;
  if (amount > 0) {
    accumulator.total += amount;
  }
  if (payment.payment_method_name && !accumulator.methodNames.includes(payment.payment_method_name)) {
    accumulator.methodNames.push(payment.payment_method_name);
  }
  if (payment.payment_method_id && !accumulator.methodIds.includes(payment.payment_method_id)) {
    accumulator.methodIds.push(payment.payment_method_id);
  }
  return accumulator;
}, { total: 0, methodNames: [], methodIds: [] });

const validateExpensePayments = (payments = [], totalAmount = 0) => {
  const invalidRow = payments.find((payment) => {
    const hasAnyValue = [payment.payment_method_id, payment.amount, payment.reference, payment.notes]
      .some((value) => String(value ?? '').trim() !== '');
    if (!hasAnyValue) return false;
    const amount = Number(payment.amount || 0);
    return !payment.payment_method_id || amount <= 0;
  });

  if (invalidRow) {
    return 'Revise os pagamentos: cada parcela precisa de forma de pagamento e valor superior a zero.';
  }

  const totalPaid = serializeExpensePayments(payments).reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  if (totalPaid > Number(totalAmount || 0) + 0.009) {
    return 'O total das formas de pagamento não pode exceder o valor da despesa.';
  }

  return null;
};

const inferExpenseCounterpartyType = (record = {}) => {
  const explicitType = String(record?.counterparty_type || '').trim();
  if (explicitType === 'employee' || record?.beneficiary_employee_id) return 'employee';
  return 'supplier';
};

const EXPENSE_SUPPLIER_REQUIRED_MESSAGE = 'Fornecedor obrigatório: seleccione um fornecedor ou clique em "Novo fornecedor" antes de guardar a despesa.';
const EXPENSE_EMPLOYEE_REQUIRED_MESSAGE = 'Colaborador obrigatório: seleccione um colaborador beneficiário antes de guardar a despesa.';

const buildPersistentOptionList = (options = [], selectedId, selectedLabel) => {
  const normalizedId = String(selectedId || '').trim();
  if (!normalizedId) return options;
  if (options.some((option) => option.id === normalizedId)) return options;
  return [{ id: normalizedId, name: selectedLabel || 'Registo original' }, ...options];
};

const normalizeOptionalEntityId = (value) => {
  const normalizedValue = String(value ?? '').trim();
  return normalizedValue || null;
};

const validateExpenseCounterpartySelection = (record = {}) => {
  const normalizedType = inferExpenseCounterpartyType(record);

  if (normalizedType === 'employee') {
    return normalizeOptionalEntityId(record?.beneficiary_employee_id)
      ? null
      : EXPENSE_EMPLOYEE_REQUIRED_MESSAGE;
  }

  return normalizeOptionalEntityId(record?.supplier_id)
    ? null
    : EXPENSE_SUPPLIER_REQUIRED_MESSAGE;
};

const unwrapSettledApiData = (result, fallback = []) => {
  if (result?.status !== 'fulfilled') {
    return fallback;
  }

  const value = result.value;
  if (Array.isArray(value)) {
    return value;
  }

  return value?.data ?? fallback;
};

const parseMultiValueParam = (value) => {
  if (!value) return [];
  return String(value).split(',').map((item) => item.trim()).filter(Boolean);
};

const serializeMultiValueParam = (values = []) => values.filter(Boolean).join(',');

const downloadCsvFile = (filename, headers, rows) => {
  const csv = [headers, ...rows]
    .map((row) => row.map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

const DEFAULT_EXPENSE_COLUMN_WIDTHS = [3, 8, 14, 7, 9, 8, 7, 8, 5, 5, 7, 7];
const MIN_EXPENSE_COLUMN_WIDTHS = [3, 6, 10, 6, 7, 6, 6, 6, 4, 4, 5, 5];
const EXPENSES_TABLE_CLASS = `${TABLE_DENSITY.table} text-[9px]`;

export default function ExpensesPage() {
  const { t } = useLanguage();
  // URL Search Params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedLocationHandle = searchParams.get('location');
  
  // Global filters
  const { filters, setFilters } = useGlobalFilters({
    period: searchParams.get('period') || 'all',
    custom_start: searchParams.get('start_date') || null,
    custom_end: searchParams.get('end_date') || null,
    location_id: searchParams.get('location_id') || null,
    status: searchParams.get('status') || null,
    category: searchParams.get('category') || null,
    payment_account_id: searchParams.get('payment_account_id') || null,
  });
  const [multiExpenseFilters, setMultiExpenseFilters] = useState({
    locations: parseMultiValueParam(searchParams.get('location_ids') || searchParams.get('location_id')),
    statuses: parseMultiValueParam(searchParams.get('status_ids') || searchParams.get('status')),
    categories: parseMultiValueParam(searchParams.get('category_ids') || searchParams.get('category')),
    subcategories: parseMultiValueParam(searchParams.get('subcategory_ids') || searchParams.get('subcategory_id')),
    paymentAccounts: parseMultiValueParam(searchParams.get('payment_account_ids') || searchParams.get('payment_account_id')),
  });
  
  // Data state
  const [expenses, setExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [createCounterpartyError, setCreateCounterpartyError] = useState('');
  const [editCounterpartyError, setEditCounterpartyError] = useState('');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [pgtAnalytics, setPgtAnalytics] = useState(null);
  const [pgt01OperationalReport, setPgt01OperationalReport] = useState(null);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState('expenses');
  const [filtersResetToken, setFiltersResetToken] = useState(0);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('all');
  
  // Search (kept separate from global filters)
  const [searchQuery, setSearchQuery] = useState('');
  const [expenseSortField, setExpenseSortField] = useState('expense_date');
  const [expenseSortDirection, setExpenseSortDirection] = useState('desc');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState([]);
  const [expenseColumnWidths, setExpenseColumnWidths] = useState(readExpenseColumnWidths);
  const [auditLogs, setAuditLogs] = useState([]);
  const expenseTableWrapperRef = useRef(null);
  const expenseColumnResizeRef = useRef(null);
  const skipNextExpensesReloadRef = useRef(false);
  
  const { hasRole, hasPermission, user } = useAuth();
  const isAdmin = hasRole(['admin']);
  const isManager = hasRole(['store_manager']);
  const isAccountant = hasRole(['accountant']);
  const canApprove = hasRole(['admin', 'store_manager', 'accountant']);
  const canCreate = hasRole(['admin', 'store_manager', 'accountant', 'staff']);
  const canReceiveInventory = hasPermission('inventory.edit') || hasRole(['admin', 'store_manager', 'accountant', 'warehouse']);
  
  // Form state for creating expense
  const initialFormData = {
    description: '',
    amount: '',
    expense_category_id: '',
    expense_subcategory_id: '',
    payment_account_id: '',
    location_id: '',
    expense_date: new Date(),
    reference_number: '',
    counterparty_type: 'supplier',
    supplier_id: '',
    beneficiary_employee_id: '',
    supplier_name: '',
    notes: '',
    payments: [createEmptyExpensePayment()],
    items: [],
    registered_at: ''  // Custom date/time
  };
  
  const [formData, setFormData] = useState(initialFormData);
  const [editFormData, setEditFormData] = useState(null);
  const [editJustification, setEditJustification] = useState('');
  const [paymentData, setPaymentData] = useState({ payments: [createEmptyExpensePayment()], notes: '' });
  const [deleteReason, setDeleteReason] = useState('');
  const [purgeReason, setPurgeReason] = useState('');
  const [renumberDialogOpen, setRenumberDialogOpen] = useState(false);
  const [renumberLoading, setRenumberLoading] = useState(false);
  const [renumberResult, setRenumberResult] = useState(null);
  const [quickCreateDialogOpen, setQuickCreateDialogOpen] = useState(false);
  const [quickCreateType, setQuickCreateType] = useState('supplier');
  const [quickCreateTarget, setQuickCreateTarget] = useState('create');
  const [expenseRollupCollapsed, setExpenseRollupCollapsed] = useState(true);
  const [createSubcategories, setCreateSubcategories] = useState([]);
  const [editSubcategories, setEditSubcategories] = useState([]);
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [quickCreateForm, setQuickCreateForm] = useState({
    name: '',
    phone: '',
    email: '',
    employee_number: '',
    role: 'staff',
    can_sell: true,
    contact_person: '',
    tax_id: '',
    address: '',
    city: 'Maputo',
    payment_terms: '30 dias',
    notes: '',
  });
  const locationHandleMaps = useMemo(() => buildHandleMaps(locations, (location) => location.name), [locations]);
  const createItemsSummary = useMemo(() => summarizeExpenseItems(formData.items), [formData.items]);
  const editItemsSummary = useMemo(() => summarizeExpenseItems(editFormData?.items || []), [editFormData?.items]);
  const createPaymentsSummary = useMemo(() => summarizeExpensePayments(serializeExpensePayments(formData.payments || [])), [formData.payments]);
  const editPaymentsSummary = useMemo(() => summarizeExpensePayments(serializeExpensePayments(editFormData?.payments || [])), [editFormData?.payments]);
  const paymentDialogSummary = useMemo(() => summarizeExpensePayments(serializeExpensePayments(paymentData.payments || [])), [paymentData.payments]);
  const editCounterpartyType = useMemo(() => inferExpenseCounterpartyType(editFormData || selectedExpense || {}), [editFormData, selectedExpense]);
  const editSupplierOptions = useMemo(() => buildPersistentOptionList(
    suppliers,
    editCounterpartyType === 'supplier' ? editFormData?.supplier_id : null,
    editFormData?.supplier_name || selectedExpense?.supplier_name,
  ), [editCounterpartyType, editFormData?.supplier_id, editFormData?.supplier_name, selectedExpense?.supplier_name, suppliers]);
  const editBeneficiaryOptions = useMemo(() => buildPersistentOptionList(
    employees,
    editCounterpartyType === 'employee' ? editFormData?.beneficiary_employee_id : null,
    editFormData?.supplier_name || selectedExpense?.supplier_name,
  ), [editCounterpartyType, editFormData?.beneficiary_employee_id, editFormData?.supplier_name, employees, selectedExpense?.supplier_name]);
  const isCreatePgtInventoryEligible = useMemo(() => {
    const selectedCategory = expenseCategories.find((category) => category.id === formData.expense_category_id && !category.parent_id);
    return selectedCategory?.expense_code === PGT01_ROOT_CODE;
  }, [expenseCategories, formData.expense_category_id]);
  const isEditPgtInventoryEligible = useMemo(() => {
    const selectedCategory = expenseCategories.find((category) => category.id === editFormData?.expense_category_id && !category.parent_id);
    return selectedCategory?.expense_code === PGT01_ROOT_CODE;
  }, [editFormData?.expense_category_id, expenseCategories]);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.period && filters.period !== 'all') params.set('period', filters.period);
    if (filters.custom_start) params.set('start_date', filters.custom_start);
    if (filters.custom_end) params.set('end_date', filters.custom_end);
    if (multiExpenseFilters.locations.length === 1) {
      applyCleanOrLegacyParam({
        params,
        cleanKey: 'location',
        legacyKey: 'location_id',
        value: multiExpenseFilters.locations[0],
        idToHandle: locationHandleMaps.idToHandle,
      });
    } else if (multiExpenseFilters.locations.length > 1) {
      params.set('location_ids', serializeMultiValueParam(multiExpenseFilters.locations));
    }
    if (multiExpenseFilters.statuses.length > 0) params.set('status_ids', serializeMultiValueParam(multiExpenseFilters.statuses));
    if (multiExpenseFilters.categories.length > 0) params.set('category_ids', serializeMultiValueParam(multiExpenseFilters.categories));
    if (multiExpenseFilters.paymentAccounts.length > 0) params.set('payment_account_ids', serializeMultiValueParam(multiExpenseFilters.paymentAccounts));
    if (multiExpenseFilters.subcategories.length > 0) params.set('subcategory_ids', serializeMultiValueParam(multiExpenseFilters.subcategories));
    setSearchParams(params, { replace: true });
  }, [filters.custom_end, filters.custom_start, filters.period, locationHandleMaps.idToHandle, multiExpenseFilters, setSearchParams]);

  useEffect(() => {
    if (!requestedLocationHandle || !locations.length || multiExpenseFilters.locations.length) return;
    const resolvedLocationId = resolveHandleOrLegacyId({
      cleanValue: requestedLocationHandle,
      legacyValue: searchParams.get('location_id'),
      handleToId: locationHandleMaps.handleToId,
    });

    if (resolvedLocationId) {
      setMultiExpenseFilters((current) => ({ ...current, locations: [resolvedLocationId] }));
    }
  }, [locationHandleMaps.handleToId, locations.length, multiExpenseFilters.locations.length, requestedLocationHandle, searchParams]);
  
  useEffect(() => {
    const hasExpenseOverlayOpen = dialogOpen || editDialogOpen || viewDialogOpen || quickCreateDialogOpen || renumberDialogOpen || payDialogOpen || deleteDialogOpen || purgeDialogOpen;
    if (!hasExpenseOverlayOpen) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPosition = body.style.position;
    const previousBodyTop = body.style.top;
    const previousBodyLeft = body.style.left;
    const previousBodyRight = body.style.right;
    const previousBodyWidth = body.style.width;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.position = previousBodyPosition;
      body.style.top = previousBodyTop;
      body.style.left = previousBodyLeft;
      body.style.right = previousBodyRight;
      body.style.width = previousBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [deleteDialogOpen, dialogOpen, editDialogOpen, payDialogOpen, purgeDialogOpen, quickCreateDialogOpen, renumberDialogOpen, viewDialogOpen]);

  useEffect(() => {
    if (!formData.items?.length) return;
    const nextAmount = createItemsSummary.total.toFixed(2);
    if (formData.amount !== nextAmount) {
      setFormData((prev) => ({ ...prev, amount: nextAmount }));
    }
  }, [createItemsSummary.total, formData.amount, formData.items]);

  useEffect(() => {
    if (!editFormData?.items?.length) return;
    const nextAmount = editItemsSummary.total.toFixed(2);
    if (editFormData.amount !== nextAmount) {
      setEditFormData((prev) => prev ? { ...prev, amount: nextAmount } : prev);
    }
  }, [editFormData, editItemsSummary.total]);

  useEffect(() => {
    if (!multiExpenseFilters.categories.length && multiExpenseFilters.subcategories.length) {
      setMultiExpenseFilters((current) => ({ ...current, subcategories: [] }));
      return;
    }

    const allowedSubcategoryIds = new Set(
      multiExpenseFilters.categories.flatMap((categoryId) => getSubcategories(categoryId).map((item) => item.id))
    );
    const hasInvalidSelection = multiExpenseFilters.subcategories.some((subcategoryId) => !allowedSubcategoryIds.has(subcategoryId));
    if (hasInvalidSelection) {
      setMultiExpenseFilters((current) => ({
        ...current,
        subcategories: current.subcategories.filter((subcategoryId) => allowedSubcategoryIds.has(subcategoryId)),
      }));
    }
  }, [expenseCategories, multiExpenseFilters.categories, multiExpenseFilters.subcategories]);

  useEffect(() => {
    if (multiExpenseFilters.statuses.length > 0) {
      setActiveTab('all');
    }
  }, [multiExpenseFilters.statuses.length]);

  const buildExpenseQueryParams = useCallback(() => {
    const params = {
      period: filters.period || 'all',
      include_cancelled: true,
    };
    if (multiExpenseFilters.statuses.length) params.status = serializeMultiValueParam(multiExpenseFilters.statuses);
    if (multiExpenseFilters.categories.length) params.category_id = serializeMultiValueParam(multiExpenseFilters.categories);
    if (multiExpenseFilters.subcategories.length) params.subcategory_id = serializeMultiValueParam(multiExpenseFilters.subcategories);
    if (multiExpenseFilters.paymentAccounts.length) params.account_id = serializeMultiValueParam(multiExpenseFilters.paymentAccounts);
    if (multiExpenseFilters.locations.length) params.location_id = serializeMultiValueParam(multiExpenseFilters.locations);
    if (filters.custom_start) params.start_date = filters.custom_start;
    if (filters.custom_end) params.end_date = filters.custom_end;
    if (searchQuery) params.search = searchQuery;
    return params;
  }, [filters.custom_end, filters.custom_start, filters.period, multiExpenseFilters, searchQuery]);

  const loadExpenseInsights = useCallback(async (params) => {
    try {
      const [summaryResponse, analyticsResponse, pgt01Response] = await Promise.all([
        expensesAPI.getSummary(params),
        expensesAPI.getPgtAnalytics({ ...params, analysis_scope: 'operational' }),
        expensesAPI.getPgt01OperationalReport({ ...params, include_cancelled: true }),
      ]);
      setSummary(summaryResponse.data || null);
      setPgtAnalytics(analyticsResponse.data || null);
      setPgt01OperationalReport(pgt01Response.data || null);
    } catch {
      setSummary(null);
      setPgtAnalytics(null);
      setPgt01OperationalReport(null);
    }
  }, []);

  const loadExpenses = useCallback(async ({ includeInsights = true } = {}) => {
    try {
      const params = buildExpenseQueryParams();
      const listResponse = await expensesAPI.list(params);
      setExpenses(listResponse.data || []);
      if (includeInsights) {
        void loadExpenseInsights(params);
      }
    } catch (error) {
      toast.error('Erro ao carregar despesas');
    }
  }, [buildExpenseQueryParams, loadExpenseInsights]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const expensesPromise = loadExpenses({ includeInsights: true });
      const primaryBootstrapPromise = Promise.allSettled([
        catalogsAPI.listExpenseCategories(),
        settingsAPI.listActivePaymentAccounts(),
        settingsAPI.listActiveLocations(),
        inventoryAPI.listProducts({ include_deleted: false, limit: 300 }),
      ]);
      const secondaryBootstrapPromise = Promise.allSettled([
        settingsAPI.listPaymentMethods(false),
        procurementAPI.listSuppliers(),
        settingsAPI.listActiveEmployees(),
      ]);
      void primaryBootstrapPromise
        .then(([categoriesResult, accountsResult, locationsResult, productsResult]) => {
          setExpenseCategories(unwrapSettledApiData(categoriesResult));
          setPaymentAccounts(unwrapSettledApiData(accountsResult));
          setLocations(unwrapSettledApiData(locationsResult));
          setInventoryProducts(unwrapSettledApiData(productsResult).filter((product) => product?.is_deleted !== true));
        })
        .catch(() => {});

      void secondaryBootstrapPromise.then(([paymentMethodsResult, suppliersResult, employeesResult]) => {
        setPaymentMethods(unwrapSettledApiData(paymentMethodsResult).filter((method) => method.is_active !== false && method.is_deleted !== true));
        setSuppliers(unwrapSettledApiData(suppliersResult));
        setEmployees(unwrapSettledApiData(employeesResult));
      }).catch(() => {});

      await expensesPromise;
      skipNextExpensesReloadRef.current = true;
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [loadExpenses]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!loading) {
      if (skipNextExpensesReloadRef.current) {
        skipNextExpensesReloadRef.current = false;
        return;
      }
      loadExpenses();
    }
  }, [loadExpenses, loading]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, [setFilters]);

  const handleResetExpenseFilters = useCallback(() => {
    setFilters({
      period: 'all',
      custom_start: null,
      custom_end: null,
      location_id: null,
      status: null,
      category: null,
      payment_account_id: null,
    });
    setMultiExpenseFilters({ locations: [], statuses: [], categories: [], subcategories: [], paymentAccounts: [] });
    setSearchQuery('');
    setActiveTab('all');
    setSelectedExpenseIds([]);
    setFiltersResetToken((current) => current + 1);
  }, [setFilters]);

  const handleStatusTabChange = useCallback((nextTab) => {
    setActiveTab(nextTab);
    if (multiExpenseFilters.statuses.length) {
      setMultiExpenseFilters((current) => ({ ...current, statuses: [] }));
    }
  }, [multiExpenseFilters.statuses.length]);

  const handlePreviewRenumberCodes = useCallback(async () => {
    try {
      setRenumberLoading(true);
      const response = await expensesAPI.renumberCodes({ dry_run: true, start_from: 1 });
      setRenumberResult(response.data);
      toast.success(`Prévia pronta: ${response.data.changed_expenses || 0} despesas serão renumeradas`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao gerar a prévia de renumeração'));
    } finally {
      setRenumberLoading(false);
    }
  }, []);

  const handleRunRenumberCodes = useCallback(async () => {
    try {
      setRenumberLoading(true);
      const response = await expensesAPI.renumberCodes({
        dry_run: false,
        start_from: 1,
        justification: 'Renumeração administrativa sequencial dos códigos de despesa',
      });
      setRenumberResult(response.data);
      setRenumberDialogOpen(false);
      await loadExpenses();
      toast.success(`Renumeração concluída: ${response.data.changed_expenses || 0} despesas ajustadas`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao renumerar os códigos de despesa'));
    } finally {
      setRenumberLoading(false);
    }
  }, [loadExpenses]);

  // ==========================================================================
  // FILTERING
  // ==========================================================================
  
  const statusInsights = useMemo(() => {
    const base = EXPENSE_STATUS_TABS.reduce((accumulator, tab) => {
      accumulator[tab.value] = { count: 0, amount: 0 };
      return accumulator;
    }, {});

    expenses.forEach((expense) => {
      const amount = Number(expense.amount || 0);
      base.all.count += 1;
      base.all.amount += amount;

      if (base[expense.status]) {
        base[expense.status].count += 1;
        base[expense.status].amount += amount;
      }
    });

    return EXPENSE_STATUS_TABS.map((tab) => ({
      ...tab,
      count: base[tab.value]?.count || 0,
      amount: base[tab.value]?.amount || 0,
    }));
  }, [expenses]);

  const filteredExpenses = useMemo(() => {
    if (!activeTab || activeTab === 'all') return expenses;
    return expenses.filter((expense) => expense.status === activeTab);
  }, [expenses, activeTab]);

  const sortedExpenses = useMemo(() => {
    const directionFactor = expenseSortDirection === 'asc' ? 1 : -1;

    const getComparableValue = (expense, field) => {
      switch (field) {
        case 'document':
          return String(expense.expense_code || '');
        case 'description':
          return String(expense.description || '');
        case 'pgt':
          return `${expense.expense_category_code || ''} ${expense.expense_category_name || ''} ${expense.expense_subcategory_code || ''} ${expense.expense_subcategory_name || ''}`.trim();
        case 'account_location':
          return `${expense.payment_account_name || ''} ${expense.location_name || ''}`.trim();
        case 'items':
          return Number(expense.items_count || 0);
        case 'amount':
          return Number(expense.amount || 0);
        case 'expense_date':
        default:
          return new Date(expense.expense_date || 0).getTime();
      }
    };

    return [...filteredExpenses].sort((left, right) => {
      const leftValue = getComparableValue(left, expenseSortField);
      const rightValue = getComparableValue(right, expenseSortField);

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * directionFactor;
      }

      return String(leftValue || '').localeCompare(String(rightValue || ''), 'pt', { sensitivity: 'base' }) * directionFactor;
    });
  }, [expenseSortDirection, expenseSortField, filteredExpenses]);

  useEffect(() => {
    setSelectedExpenseIds((current) => current.filter((id) => filteredExpenses.some((expense) => expense.id === id)));
  }, [filteredExpenses]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    writeExpenseColumnWidths(expenseColumnWidths);
  }, [expenseColumnWidths]);

  const allVisibleExpensesSelected = sortedExpenses.length > 0 && sortedExpenses.every((expense) => selectedExpenseIds.includes(expense.id));

  const startExpenseColumnResize = useCallback((index, event) => {
    const wrapperWidth = expenseTableWrapperRef.current?.offsetWidth || 1;
    expenseColumnResizeRef.current = {
      index,
      startX: event.clientX,
      startWidths: [...expenseColumnWidths],
      wrapperWidth,
    };

    const onMouseMove = (moveEvent) => {
      const state = expenseColumnResizeRef.current;
      if (!state) return;
      const deltaPercent = ((moveEvent.clientX - state.startX) / state.wrapperWidth) * 100;
      const next = [...state.startWidths];
      const nextCurrent = Math.max(MIN_EXPENSE_COLUMN_WIDTHS[index], state.startWidths[index] + deltaPercent);
      const nextRight = Math.max(MIN_EXPENSE_COLUMN_WIDTHS[index + 1], state.startWidths[index + 1] - deltaPercent);
      next[index] = nextCurrent;
      next[index + 1] = nextRight;
      setExpenseColumnWidths(next);
    };

    const onMouseUp = () => {
      expenseColumnResizeRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [expenseColumnWidths]);

  const toggleExpenseSelection = useCallback((expenseId, checked) => {
    setSelectedExpenseIds((current) => (checked
      ? (current.includes(expenseId) ? current : [...current, expenseId])
      : current.filter((id) => id !== expenseId)));
  }, []);

  const toggleAllVisibleExpenses = useCallback((checked) => {
    setSelectedExpenseIds(checked ? sortedExpenses.map((expense) => expense.id) : []);
  }, [sortedExpenses]);

  const getExpenseAccountLabel = useCallback((expense) => {
    if (expense?.payment_account_name) return expense.payment_account_name;
    const paymentMethods = [
      ...(expense?.payment_methods_summary || []),
      ...((expense?.payments || []).map((payment) => payment.payment_method_name).filter(Boolean)),
    ];
    const uniqueMethods = [...new Set(paymentMethods.map((method) => String(method || '').trim()).filter(Boolean))];
    if (uniqueMethods.length > 1) return 'Múltiplas Contas';
    return 'Sem conta';
  }, []);

  const handleBulkNormalizeExpenses = useCallback(async () => {
    const eligibleIds = sortedExpenses
      .filter((expense) => selectedExpenseIds.includes(expense.id) && ['pending_approval', 'approved'].includes(expense.status))
      .map((expense) => expense.id);

    if (!eligibleIds.length) {
      toast.error('Seleccione despesas legacy pendentes/aprovadas para normalização em massa');
      return;
    }

    try {
      const results = await Promise.allSettled(eligibleIds.map((expenseId) => expensesAPI.approve(expenseId, 'Normalização em massa')));
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - successCount;
      if (successCount) {
        toast.success(`${successCount} despesa(s) normalizada(s)`);
      }
      if (failedCount) {
        toast.warning(`${failedCount} despesa(s) não puderam ser normalizadas`);
      }
      setSelectedExpenseIds([]);
      await loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na normalização em massa das despesas'));
    }
  }, [loadExpenses, selectedExpenseIds, sortedExpenses]);

  const handleExportSelectedExpenses = useCallback(() => {
    if (!selectedExpenseIds.length) {
      toast.error('Seleccione despesas para exportar');
      return;
    }

    const selected = sortedExpenses.filter((expense) => selectedExpenseIds.includes(expense.id));
    if (!selected.length) {
      toast.error('Nenhuma despesa seleccionada para exportação');
      return;
    }

    downloadCsvFile(
      `despesas_seleccionadas_${new Date().toISOString().slice(0, 10)}.csv`,
      ['Código', 'Descrição', 'Estado', 'Categoria', 'Subcategoria', 'Conta', 'Localização', 'Valor', 'Data'],
      selected.map((expense) => [
        expense.expense_code || '',
        expense.description || '',
        EXPENSES_STATUS_LABELS[expense.status] || expense.status || '',
        expense.expense_category_name || '',
        expense.expense_subcategory_name || '',
        getExpenseAccountLabel(expense),
        expense.location_name || '',
        Number(expense.amount || 0).toFixed(2),
        formatDate(expense.expense_date),
      ]),
    );
    toast.success(`${selected.length} despesa(s) exportada(s)`);
  }, [getExpenseAccountLabel, selectedExpenseIds, sortedExpenses]);

  const statusCounts = useMemo(() => (
    statusInsights.reduce((accumulator, tab) => {
      accumulator[tab.value] = tab.count;
      return accumulator;
    }, {})
  ), [statusInsights]);

  const activeStatusInsight = useMemo(() => (
    statusInsights.find((tab) => tab.value === activeTab) || statusInsights[0] || EXPENSE_STATUS_TABS[0]
  ), [activeTab, statusInsights]);

  const pgt01OperationalSummaryCards = useMemo(() => {
    const reportSummary = pgt01OperationalReport?.summary;
    if (!reportSummary) return [];
    return [
      { key: 'linked', label: 'Despesas ligadas', value: reportSummary.linked_expenses || 0, accent: 'text-slate-900' },
      { key: 'pending', label: 'Recepções pendentes', value: reportSummary.pending_receipts || 0, accent: 'text-amber-700' },
      { key: 'received', label: 'Recepções confirmadas', value: reportSummary.received_expenses || 0, accent: 'text-emerald-700' },
      { key: 'items', label: 'Itens ligados', value: reportSummary.linked_items || 0, accent: 'text-blue-700' },
    ];
  }, [pgt01OperationalReport]);


  const visibleExpenseAmount = useMemo(() => (
    filteredExpenses.reduce((total, expense) => total + Number(expense.amount || 0), 0)
  ), [filteredExpenses]);

  const getExpensePaymentMethodsLabel = useCallback((expense) => {
    const methods = expense?.payment_methods_summary || [];
    if (methods.length === 0) return 'Sem pagamentos';
    if (methods.length === 1) return methods[0];
    return `${methods.slice(0, 2).join(' + ')}${methods.length > 2 ? ` +${methods.length - 2}` : ''}`;
  }, []);

  const getExpensePaymentBalance = useCallback((expense) => {
    const explicitBalance = Number(expense?.payment_balance);
    if (Number.isFinite(explicitBalance)) return explicitBalance;
    return Math.max(Number(expense?.amount || 0) - Number(expense?.payments_total || 0), 0);
  }, []);

  const getExpenseSettlementLabel = useCallback((expense) => {
    const totalPaid = Number(expense?.payments_total || 0);
    const balance = getExpensePaymentBalance(expense);
    if (totalPaid <= 0) return 'Sem liquidação';
    if (balance <= 0) return 'Liquidada';
    return `Pago ${formatCurrency(totalPaid)} · Saldo ${formatCurrency(balance)}`;
  }, [getExpensePaymentBalance]);

  const expenseSortLabel = useMemo(
    () => EXPENSE_SORT_OPTIONS.find((option) => option.value === expenseSortField)?.label || 'Data',
    [expenseSortField],
  );

  const handleExpenseSort = useCallback((field) => {
    if (expenseSortField === field) {
      setExpenseSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setExpenseSortField(field);
    setExpenseSortDirection(field === 'expense_date' || field === 'amount' || field === 'items' ? 'desc' : 'asc');
  }, [expenseSortField]);

  const renderExpenseSortIndicator = useCallback((field) => {
    if (expenseSortField !== field) {
      return <ChevronDown className="h-3.5 w-3.5 text-slate-300" />;
    }

    return expenseSortDirection === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-slate-700" />
      : <ChevronDown className="h-3.5 w-3.5 text-slate-700" />;
  }, [expenseSortDirection, expenseSortField]);

  const expenseItemRollup = useMemo(() => {
    const grouped = new Map();
    filteredExpenses.forEach((expense) => {
      (expense.items || []).forEach((item) => {
        const key = `${expense.location_id || 'none'}::${String(item.description || '').trim().toLowerCase()}::${item.unit || 'un'}`;
        const current = grouped.get(key) || {
          key,
          description: item.description,
          unit: item.unit || 'un',
          location_name: expense.location_name || 'Sem localização',
          quantity: 0,
          used_quantity: 0,
          remaining_quantity: 0,
          total: 0,
          documents_count: 0,
        };
        current.quantity += Number(item.quantity || 0);
        current.used_quantity += Number(item.used_quantity || 0);
        current.remaining_quantity += Number(item.remaining_quantity || 0);
        current.total += Number(item.total || 0);
        current.documents_count += 1;
        grouped.set(key, current);
      });
    });
    return Array.from(grouped.values()).sort((left, right) => right.total - left.total).slice(0, 8);
  }, [filteredExpenses]);

  // ==========================================================================
  // CRUD ACTIONS
  // ==========================================================================
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentSubcategories = currentCreateSubcategories;
    const itemsError = validateExpenseItems(formData.items);
    const paymentsError = validateExpensePayments(formData.payments, formData.amount);
    
    if (!formData.description || !formData.amount || !formData.expense_category_id || !formData.location_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (itemsError) {
      toast.error(itemsError);
      return;
    }

    if (paymentsError) {
      toast.error(paymentsError);
      return;
    }

    if (currentSubcategories.length > 0 && !formData.expense_subcategory_id) {
      toast.error('Subcategoria é obrigatória para a categoria seleccionada');
      return;
    }

    const counterpartyError = validateExpenseCounterpartySelection(formData);
    if (counterpartyError) {
      setCreateCounterpartyError(counterpartyError);
      toast.error(counterpartyError);
      return;
    }

    setCreateCounterpartyError('');
    
    try {
      const normalizedCounterpartyType = inferExpenseCounterpartyType(formData);
      const selectedSupplier = suppliers.find((item) => item.id === formData.supplier_id);
      const selectedBeneficiary = employees.find((item) => item.id === formData.beneficiary_employee_id);
      const expenseData = {
        ...formData,
        payment_account_id: normalizeOptionalEntityId(formData.payment_account_id),
        counterparty_type: normalizedCounterpartyType,
        supplier_id: normalizedCounterpartyType === 'supplier' ? normalizeOptionalEntityId(formData.supplier_id) : null,
        beneficiary_employee_id: normalizedCounterpartyType === 'employee' ? normalizeOptionalEntityId(formData.beneficiary_employee_id) : null,
        supplier_name: normalizedCounterpartyType === 'supplier'
          ? (selectedSupplier?.name || formData.supplier_name || '')
          : (selectedBeneficiary?.name || formData.supplier_name || ''),
        amount: parseFloat(formData.amount),
        payments: serializeExpensePayments(formData.payments),
        items: serializeExpenseItems(formData.items),
        expense_date: formData.expense_date.toISOString(),
        registered_at: formData.registered_at ? new Date(formData.registered_at).toISOString() : null
      };
      
      const response = await expensesAPI.create(expenseData);
      const createdExpense = response.data;
      toast.success(createdExpense?.status === 'paid' ? 'Despesa criada já liquidada!' : 'Despesa registada com sucesso!');
      setCreateCounterpartyError('');
      setDialogOpen(false);
      setCreateSubcategories([]);
      setFormData({ ...initialFormData, payments: [createEmptyExpensePayment()] });
      setSelectedExpense(createdExpense);
      setViewDialogOpen(true);
      loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao criar despesa'));
    }
  };
  
  const handleView = async (expense) => {
    setSelectedExpense(expense);
    setViewDialogOpen(true);
  };
  
  const handleEdit = async (expense) => {
    const canEditPaidExpense = expense.status === 'paid' && isAdmin;
    if (!['draft', 'registered'].includes(expense.status) && !canEditPaidExpense) {
      toast.error(expense.status === 'paid'
        ? 'Apenas administradores podem editar despesas já pagas'
        : 'Apenas despesas em rascunho ou registadas podem ser editadas');
      return;
    }
    setSelectedExpense(expense);
    setEditSubcategories([]);
    setEditCounterpartyError('');
    setEditFormData({
      description: expense.description,
      amount: expense.amount?.toString() || '',
      expense_category_id: expense.expense_category_id,
      expense_subcategory_id: expense.expense_subcategory_id || '',
      payment_account_id: expense.payment_account_id || '',
      location_id: expense.location_id,
      expense_date: new Date(expense.expense_date),
      registered_at: expense.registered_at ? new Date(expense.registered_at).toISOString().slice(0, 16) : '',
      reference_number: expense.reference_number || '',
      counterparty_type: inferExpenseCounterpartyType(expense),
      supplier_id: expense.supplier_id || '',
      beneficiary_employee_id: expense.beneficiary_employee_id || '',
      supplier_name: expense.supplier_name || '',
      notes: expense.notes || '',
      payments: hydrateExpensePayments(expense.payments || []),
      items: hydrateExpenseItems(expense.items || []),
    });
    setEditJustification('');
    setViewDialogOpen(false);
    setEditDialogOpen(true);
  };
  
  const handleSaveEdit = async () => {
    if (!selectedExpense?.id || !editFormData) {
      toast.error('Despesa inválida para edição');
      return;
    }

    const currentSubcategories = currentEditSubcategories;
    const itemsError = validateExpenseItems(editFormData.items || []);
    const paymentsError = validateExpensePayments(editFormData.payments || [], editFormData.amount);
    if (!editJustification || editJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }

    if (!editFormData.description || !editFormData.amount || !editFormData.expense_category_id || !editFormData.location_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (itemsError) {
      toast.error(itemsError);
      return;
    }

    if (paymentsError) {
      toast.error(paymentsError);
      return;
    }

    if (currentSubcategories.length > 0 && !editFormData.expense_subcategory_id) {
      toast.error('Subcategoria é obrigatória para a categoria seleccionada');
      return;
    }

    const counterpartyError = validateExpenseCounterpartySelection(editFormData);
    if (counterpartyError) {
      setEditCounterpartyError(counterpartyError);
      toast.error(counterpartyError);
      return;
    }

    setEditCounterpartyError('');
    
    try {
      const normalizedCounterpartyType = inferExpenseCounterpartyType(editFormData);
      const selectedSupplier = editSupplierOptions.find((item) => item.id === editFormData.supplier_id);
      const selectedBeneficiary = editBeneficiaryOptions.find((item) => item.id === editFormData.beneficiary_employee_id);
      const response = await expensesAPI.update(selectedExpense.id, {
        ...editFormData,
        payment_account_id: normalizeOptionalEntityId(editFormData.payment_account_id),
        counterparty_type: normalizedCounterpartyType,
        supplier_id: normalizedCounterpartyType === 'supplier' ? normalizeOptionalEntityId(editFormData.supplier_id) : null,
        beneficiary_employee_id: normalizedCounterpartyType === 'employee' ? normalizeOptionalEntityId(editFormData.beneficiary_employee_id) : null,
        supplier_name: normalizedCounterpartyType === 'supplier'
          ? (selectedSupplier?.name || editFormData.supplier_name || selectedExpense?.supplier_name || '')
          : (selectedBeneficiary?.name || editFormData.supplier_name || selectedExpense?.supplier_name || ''),
        amount: parseFloat(editFormData.amount),
        payments: serializeExpensePayments(editFormData.payments || []),
        items: serializeExpenseItems(editFormData.items || []),
        expense_date: editFormData.expense_date.toISOString(),
        registered_at: editFormData.registered_at ? new Date(editFormData.registered_at).toISOString() : null,
        justification: editJustification.trim()
      });

      const updatedExpense = response.data;
      if (selectedExpense?.status === 'paid') {
        toast.success(updatedExpense?.status === 'paid'
          ? 'Despesa paga actualizada com sucesso!'
          : 'Despesa actualizada e reaberta para regularização.');
      } else {
        toast.success('Despesa actualizada!');
      }
      setEditCounterpartyError('');
      setSelectedExpense(updatedExpense);
      setEditDialogOpen(false);
      loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao actualizar despesa'));
    }
  };
  
  const handleSubmitForApproval = async (expense) => {
    try {
      await expensesAPI.submit(expense.id);
      toast.success('Despesa registada sem aprovação manual!');
      loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao registar despesa'));
    }
  };

  const handleNormalizeLegacyExpense = async (expense) => {
    try {
      await expensesAPI.approve(expense.id, 'Fluxo manual de aprovação removido');
      toast.success('Despesa legacy convertida para registada!');
      loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao converter despesa legacy'));
    }
  };
  
  const handleOpenPay = (expense) => {
    const remainingBalance = Math.max(Number(expense.payment_balance ?? (Number(expense.amount || 0) - Number(expense.payments_total || 0))), 0);
    setSelectedExpense(expense);
    setPaymentData({
      payments: [{ ...createEmptyExpensePayment(), amount: remainingBalance ? remainingBalance.toFixed(2) : '' }],
      notes: '',
    });
    setPayDialogOpen(true);
  };
  
  const handlePaySubmit = async () => {
    if (!selectedExpense?.id) {
      toast.error('Despesa inválida para pagamento');
      return;
    }

    const selectedExpenseBalance = Number(selectedExpense.payment_balance ?? Math.max(Number(selectedExpense.amount || 0) - Number(selectedExpense.payments_total || 0), 0));
    const paymentsError = validateExpensePayments(paymentData.payments || [], selectedExpenseBalance);
    if (paymentsError) {
      toast.error(paymentsError);
      return;
    }

    const paymentsPayload = serializeExpensePayments(paymentData.payments || []);
    if (paymentsPayload.length === 0) {
      toast.error('Adicione pelo menos uma forma de pagamento');
      return;
    }

    try {
      const response = await expensesAPI.markPaid(selectedExpense.id, {
        payments: paymentsPayload,
        notes: paymentData.notes?.trim() || null,
      });
      toast.success(response.data?.status === 'paid' ? 'Despesa liquidada com sucesso!' : 'Pagamento registado com saldo em aberto.');
      setPayDialogOpen(false);
      loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao registar pagamento'));
    }
  };

  const handleOpenDelete = (expense) => {
    setSelectedExpense(expense);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteSubmit = async () => {
    if (!selectedExpense?.id) {
      toast.error('Despesa inválida para cancelamento');
      return;
    }

    if (!deleteReason || deleteReason.trim().length < 5) {
      toast.error('Motivo de cancelamento obrigatório');
      return;
    }
    
    try {
      await expensesAPI.delete(selectedExpense.id, deleteReason.trim());
      toast.success('Despesa movida para a tab Canceladas!');
      setDeleteDialogOpen(false);
      loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao cancelar despesa'));
    }
  };

  const handleConfirmInventoryReceipt = async (expense) => {
    if (!expense?.id) {
      toast.error('Despesa inválida para recepção');
      return;
    }
    try {
      const response = await expensesAPI.receiveInventory(expense.id, {});
      toast.success(response.data?.message || 'Recepção/entrada em stock confirmada!');
      setSelectedExpense(response.data?.expense || expense);
      await loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao confirmar recepção no inventário'));
    }
  };

  const handleCancelInventorySync = async (expense) => {
    if (!expense?.id) {
      toast.error('Despesa inválida para anulação');
      return;
    }
    const reason = window.prompt('Motivo da anulação controlada:');
    if (!reason || reason.trim().length < 5) {
      toast.error('Motivo mínimo de 5 caracteres');
      return;
    }
    try {
      const response = await expensesAPI.cancelInventory(expense.id, reason.trim());
      toast.success(response.data?.message || 'Despesa anulada com reversão controlada');
      setSelectedExpense(response.data?.expense || expense);
      setViewDialogOpen(false);
      await loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao anular integração com inventário'));
    }
  };

  const handleOpenPermanentDelete = (expense) => {
    setSelectedExpense(expense);
    setPurgeReason('');
    setPurgeDialogOpen(true);
  };

  const handlePermanentDeleteSubmit = async () => {
    if (!selectedExpense?.id) {
      toast.error('Despesa inválida para eliminação permanente');
      return;
    }

    if (!purgeReason || purgeReason.trim().length < 5) {
      toast.error('Motivo da eliminação permanente obrigatório');
      return;
    }

    try {
      await expensesAPI.permanentDelete(selectedExpense.id, purgeReason.trim());
      toast.success('Despesa eliminada permanentemente!');
      setPurgeDialogOpen(false);
      setViewDialogOpen(false);
      loadExpenses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar despesa permanentemente'));
    }
  };
  
  const handleViewAudit = async (expense) => {
    setSelectedExpense(expense);
    try {
      const response = await expensesAPI.getAuditLog(expense.id);
      setAuditLogs(response.data || []);
      setAuditDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleQuickCreateCounterparty = async () => {
    if (!quickCreateForm.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      if (quickCreateType === 'supplier') {
        const response = await procurementAPI.createSupplier({
          name: quickCreateForm.name.trim(),
          contact_person: quickCreateForm.contact_person || null,
          tax_id: quickCreateForm.tax_id || null,
          phone: quickCreateForm.phone || null,
          email: quickCreateForm.email || null,
          address: quickCreateForm.address || null,
          city: quickCreateForm.city || null,
          payment_terms: quickCreateForm.payment_terms || null,
          notes: quickCreateForm.notes || null,
        });
        const createdSupplier = response.data;
        setSuppliers(prev => [...prev, createdSupplier].sort((a, b) => a.name.localeCompare(b.name)));
        const applySelection = (prev) => ({
          ...prev,
          counterparty_type: 'supplier',
          supplier_id: createdSupplier.id,
          beneficiary_employee_id: '',
          supplier_name: createdSupplier.name,
        });
        if (quickCreateTarget === 'edit') setEditFormData(applySelection);
        else setFormData(applySelection);
      } else {
        const response = await settingsAPI.createEmployee({
          name: quickCreateForm.name.trim(),
          employee_number: quickCreateForm.employee_number || null,
          phone: quickCreateForm.phone || null,
          email: quickCreateForm.email || null,
          role: quickCreateForm.role || 'staff',
          can_sell: Boolean(quickCreateForm.can_sell),
        });
        const createdEmployee = response.data;
        setEmployees(prev => [...prev, createdEmployee].sort((a, b) => a.name.localeCompare(b.name)));
        const applySelection = (prev) => ({
          ...prev,
          counterparty_type: 'employee',
          beneficiary_employee_id: createdEmployee.id,
          supplier_id: '',
          supplier_name: createdEmployee.name,
        });
        if (quickCreateTarget === 'edit') setEditFormData(applySelection);
        else setFormData(applySelection);
      }

      toast.success(quickCreateType === 'supplier' ? 'Fornecedor criado e seleccionado' : 'Colaborador criado e seleccionado');
      setQuickCreateDialogOpen(false);
      setQuickCreateForm({ name: '', phone: '', email: '', employee_number: '', role: 'staff', can_sell: true, contact_person: '', tax_id: '', address: '', city: 'Maputo', payment_terms: '30 dias', notes: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao criar registo'));
    }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  // Get subcategories for selected category
  const getSubcategories = (categoryId) => {
    return expenseCategories.find(c => c.id === categoryId)?.subcategories || [];
  };

  const loadSubcategoriesForCategory = useCallback(async (categoryId) => {
    if (!categoryId) return [];

    try {
      const response = await expensesAPI.listSubcategories(categoryId);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return getSubcategories(categoryId);
    }
  }, [expenseCategories]);
  
  // Get top-level categories
  const topLevelCategories = useMemo(() => {
    return (expenseCategories || []).filter((category) => category && !category.parent_id);
  }, [expenseCategories]);

  const categoryFilterOptions = useMemo(() => (
    topLevelCategories.map((category) => ({
      value: category.id,
      label: `${category.expense_code} · ${category.name}`,
    }))
  ), [topLevelCategories]);

  const quickFilterSubcategories = useMemo(() => {
    if (!multiExpenseFilters.categories.length) return [];
    const seen = new Set();
    return multiExpenseFilters.categories.flatMap((categoryId) => getSubcategories(categoryId)).filter((subcategory) => {
      if (!subcategory?.id || seen.has(subcategory.id)) return false;
      seen.add(subcategory.id);
      return true;
    });
  }, [expenseCategories, multiExpenseFilters.categories]);

  const activeExpenseFilterChips = useMemo(() => {
    const chips = [];

    if (filters.period && filters.period !== 'all') {
      chips.push({ key: 'period', label: EXPENSE_PERIOD_LABELS[filters.period] || filters.period });
    }

    if (filters.period === 'custom' && (filters.custom_start || filters.custom_end)) {
      chips.push({ key: 'custom_range', label: `Intervalo: ${filters.custom_start || '...'} → ${filters.custom_end || '...'}` });
    }

    if (multiExpenseFilters.locations.length) {
      chips.push({
        key: 'location',
        label: `Localizações: ${multiExpenseFilters.locations.map((locationId) => locations.find((location) => location.id === locationId)?.name || locationId).join(', ')}`,
      });
    }

    if (multiExpenseFilters.statuses.length) {
      chips.push({ key: 'status', label: `Estados: ${multiExpenseFilters.statuses.map((status) => EXPENSES_STATUS_LABELS[status] || status).join(', ')}` });
    }

    if (multiExpenseFilters.categories.length) {
      chips.push({
        key: 'category',
        label: `Categorias: ${multiExpenseFilters.categories.map((categoryId) => {
          const category = topLevelCategories.find((item) => item.id === categoryId);
          return category ? `${category.expense_code} · ${category.name}` : categoryId;
        }).join(', ')}`,
      });
    }

    if (multiExpenseFilters.subcategories.length) {
      chips.push({
        key: 'subcategory',
        label: `Subcategorias: ${multiExpenseFilters.subcategories.map((subcategoryId) => {
          const subcategory = quickFilterSubcategories.find((item) => item.id === subcategoryId);
          return subcategory ? `${subcategory.expense_code} · ${subcategory.name}` : subcategoryId;
        }).join(', ')}`,
      });
    }

    if (multiExpenseFilters.paymentAccounts.length) {
      chips.push({
        key: 'account',
        label: `Contas: ${multiExpenseFilters.paymentAccounts.map((accountId) => paymentAccounts.find((account) => account.id === accountId)?.name || accountId).join(', ')}`,
      });
    }

    if (searchQuery.trim()) {
      chips.push({ key: 'search', label: `Pesquisa: ${searchQuery.trim()}` });
    }

    return chips;
  }, [filters.custom_end, filters.custom_start, filters.period, locations, multiExpenseFilters, paymentAccounts, quickFilterSubcategories, searchQuery, topLevelCategories]);

  const renumberPreviewItems = useMemo(() => (
    (renumberResult?.mappings || []).filter((item) => item.changed).slice(0, 5)
  ), [renumberResult]);

  useEffect(() => {
    let ignore = false;

    if (!formData.expense_category_id) {
      setCreateSubcategories([]);
      return undefined;
    }

    (async () => {
      const subcategories = await loadSubcategoriesForCategory(formData.expense_category_id);
      if (ignore) return;
      setCreateSubcategories(subcategories);
      if (formData.expense_subcategory_id && !subcategories.some((subcategory) => subcategory.id === formData.expense_subcategory_id)) {
        setFormData((prev) => prev.expense_category_id === formData.expense_category_id ? { ...prev, expense_subcategory_id: '' } : prev);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [formData.expense_category_id, loadSubcategoriesForCategory]);

  useEffect(() => {
    let ignore = false;

    if (!editFormData?.expense_category_id) {
      setEditSubcategories([]);
      return undefined;
    }

    (async () => {
      const subcategories = await loadSubcategoriesForCategory(editFormData.expense_category_id);
      if (ignore) return;
      setEditSubcategories(subcategories);
      if (editFormData.expense_subcategory_id && !subcategories.some((subcategory) => subcategory.id === editFormData.expense_subcategory_id)) {
        setEditFormData((prev) => prev?.expense_category_id === editFormData.expense_category_id ? { ...prev, expense_subcategory_id: '' } : prev);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [editFormData?.expense_category_id, loadSubcategoriesForCategory]);

  const currentCreateSubcategories = createSubcategories.length > 0
    ? createSubcategories
    : getSubcategories(formData.expense_category_id);

  const currentEditSubcategories = editSubcategories.length > 0
    ? editSubcategories
    : getSubcategories(editFormData?.expense_category_id);

  const handleCreateItemsChange = useCallback((updater) => {
    setFormData((prev) => ({ ...prev, items: updater(prev.items || []).map(calculateExpenseItemPreview) }));
  }, []);

  const handleEditItemsChange = useCallback((updater) => {
    setEditFormData((prev) => prev ? { ...prev, items: updater(prev.items || []).map(calculateExpenseItemPreview) } : prev);
  }, []);

  const addCreateExpenseItem = useCallback(() => {
    handleCreateItemsChange((items) => [...items, createEmptyExpenseItem()]);
  }, [handleCreateItemsChange]);

  const addEditExpenseItem = useCallback(() => {
    handleEditItemsChange((items) => [...items, createEmptyExpenseItem()]);
  }, [handleEditItemsChange]);

  const updateCreateExpenseItem = useCallback((itemId, field, value) => {
    handleCreateItemsChange((items) => items.map((item) => {
      if (item.id !== itemId) return item;
      const nextItem = { ...item, [field]: value };
      if (field === 'inventory_linked' && !value) {
        nextItem.inventory_product_id = '';
        nextItem.inventory_location_id = '';
        nextItem.inventory_status = 'not_linked';
      }
      if (field === 'inventory_linked' && value) {
        nextItem.inventory_status = item.inventory_status === 'received' ? 'received' : 'pending_receipt';
      }
      return nextItem;
    }));
  }, [handleCreateItemsChange]);

  const updateEditExpenseItem = useCallback((itemId, field, value) => {
    handleEditItemsChange((items) => items.map((item) => {
      if (item.id !== itemId) return item;
      const nextItem = { ...item, [field]: value };
      if (field === 'inventory_linked' && !value) {
        nextItem.inventory_product_id = '';
        nextItem.inventory_location_id = '';
        nextItem.inventory_status = 'not_linked';
      }
      if (field === 'inventory_linked' && value) {
        nextItem.inventory_status = item.inventory_status === 'received' ? 'received' : 'pending_receipt';
      }
      return nextItem;
    }));
  }, [handleEditItemsChange]);

  const removeCreateExpenseItem = useCallback((itemId) => {
    handleCreateItemsChange((items) => items.filter((item) => item.id !== itemId));
  }, [handleCreateItemsChange]);

  const removeEditExpenseItem = useCallback((itemId) => {
    handleEditItemsChange((items) => items.filter((item) => item.id !== itemId));
  }, [handleEditItemsChange]);

  const addCreateExpensePayment = useCallback(() => {
    setFormData((prev) => ({ ...prev, payments: [...(prev.payments || []), createEmptyExpensePayment()] }));
  }, []);

  const addEditExpensePayment = useCallback(() => {
    setEditFormData((prev) => prev ? { ...prev, payments: [...(prev.payments || []), createEmptyExpensePayment()] } : prev);
  }, []);

  const addDialogExpensePayment = useCallback(() => {
    setPaymentData((prev) => ({ ...prev, payments: [...(prev.payments || []), createEmptyExpensePayment()] }));
  }, []);

  const updateCreateExpensePayment = useCallback((index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      payments: (prev.payments || []).map((payment, currentIndex) => (
        currentIndex === index ? { ...payment, [field]: value } : payment
      )),
    }));
  }, []);

  const updateEditExpensePayment = useCallback((index, field, value) => {
    setEditFormData((prev) => prev ? {
      ...prev,
      payments: (prev.payments || []).map((payment, currentIndex) => (
        currentIndex === index ? { ...payment, [field]: value } : payment
      )),
    } : prev);
  }, []);

  const updateDialogExpensePayment = useCallback((index, field, value) => {
    setPaymentData((prev) => ({
      ...prev,
      payments: (prev.payments || []).map((payment, currentIndex) => (
        currentIndex === index ? { ...payment, [field]: value } : payment
      )),
    }));
  }, []);

  const removeCreateExpensePayment = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      payments: (prev.payments || []).length > 1
        ? prev.payments.filter((_, currentIndex) => currentIndex !== index)
        : prev.payments,
    }));
  }, []);

  const removeEditExpensePayment = useCallback((index) => {
    setEditFormData((prev) => prev ? {
      ...prev,
      payments: (prev.payments || []).length > 1
        ? prev.payments.filter((_, currentIndex) => currentIndex !== index)
        : prev.payments,
    } : prev);
  }, []);

  const removeDialogExpensePayment = useCallback((index) => {
    setPaymentData((prev) => ({
      ...prev,
      payments: (prev.payments || []).length > 1
        ? prev.payments.filter((_, currentIndex) => currentIndex !== index)
        : prev.payments,
    }));
  }, []);

  const handleCreateDialogOpenChange = useCallback((open) => {
    if (!open) {
      setCreateCounterpartyError('');
    }
    setDialogOpen(open);
  }, []);

  const canEditExpense = useCallback((expense) => {
    if (!expense) return false;
    if (['received', 'reversal_blocked'].includes(expense.inventory_sync_status)) return false;
    return ['draft', 'registered'].includes(expense?.status) || (expense?.status === 'paid' && isAdmin);
  }, [isAdmin]);

  const renderExpenseActionButtons = (expense) => (
    <div className="flex items-center justify-end gap-1.5" data-testid={`expense-actions-${expense.id}`}>
      <Button
        size="icon"
        variant="outline"
        className="h-8 w-8 rounded-lg"
        onClick={() => handleView(expense)}
        title="Ver Detalhes"
        data-testid={`expense-view-button-${expense.id}`}
      >
        <Eye className="h-4 w-4" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8 rounded-lg border-slate-200 bg-slate-50 text-slate-700 hover:bg-white"
            title="Mais acções"
            data-testid={`expense-actions-menu-trigger-${expense.id}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-2xl border border-slate-200 bg-white p-1" data-testid={`expense-actions-menu-${expense.id}`}>
          <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Acções</DropdownMenuLabel>
          {canReceiveInventory && expense.inventory_sync_status === 'pending_receipt' && (
            <>
              <DropdownMenuItem onClick={() => handleConfirmInventoryReceipt(expense)} data-testid={`expense-inventory-receive-button-${expense.id}`}>
                <Warehouse className="h-4 w-4 text-blue-600" /> Confirmar entrada em stock
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {canReceiveInventory && ['pending_receipt', 'received', 'reversal_blocked'].includes(expense.inventory_sync_status) && expense.inventory_linked_items_count > 0 && (
            <>
              <DropdownMenuItem onClick={() => handleCancelInventorySync(expense)} data-testid={`expense-inventory-cancel-button-${expense.id}`}>
                <RotateCcw className="h-4 w-4 text-red-600" /> Anular com histórico
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {canEditExpense(expense) && (
            <>
              <DropdownMenuItem onClick={() => handleEdit(expense)} data-testid={`expense-edit-button-${expense.id}`}>
                <Edit className="h-4 w-4 text-amber-600" /> Editar
              </DropdownMenuItem>
              {expense.status === 'draft' && (
                <DropdownMenuItem onClick={() => handleSubmitForApproval(expense)} data-testid={`expense-register-button-${expense.id}`}>
                  <Send className="h-4 w-4 text-blue-600" /> Registar despesa
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => handleOpenDelete(expense)} data-testid={`expense-cancel-button-${expense.id}`}>
                <Trash2 className="h-4 w-4 text-red-600" /> Cancelar despesa
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {expense.status === 'pending_approval' && canApprove && (
            <>
              <DropdownMenuItem onClick={() => handleNormalizeLegacyExpense(expense)} data-testid={`expense-register-legacy-button-${expense.id}`}>
                <Check className="h-4 w-4 text-green-600" /> Converter para registada
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleOpenDelete(expense)} data-testid={`expense-reject-button-${expense.id}`}>
                <X className="h-4 w-4 text-red-600" /> Cancelar legado
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {['registered', 'approved'].includes(expense.status) && canApprove && getExpensePaymentBalance(expense) > 0 && (
            <>
              <DropdownMenuItem onClick={() => handleOpenPay(expense)} data-testid={`expense-pay-button-${expense.id}`}>
                <CreditCard className="h-4 w-4 text-emerald-600" /> Registar pagamento
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {expense.status === 'cancelled' && isAdmin && (
            <>
              <DropdownMenuItem onClick={() => handleOpenPermanentDelete(expense)} data-testid={`expense-permanent-delete-button-${expense.id}`}>
                <Trash2 className="h-4 w-4 text-red-700" /> Eliminar permanentemente
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem onClick={() => handleViewAudit(expense)} data-testid={`expense-audit-button-${expense.id}`}>
            <History className="h-4 w-4 text-indigo-600" /> Histórico
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const renderCreateExpenseForm = () => {
    const showStep = () => true;

    return (
      <form
        onSubmit={handleSubmit}
        className="flex h-full max-h-[inherit] flex-col bg-white text-sm [&_input]:text-sm [&_textarea]:text-sm [&_button]:text-sm"
        data-testid="add-expense-form"
      >
        <DialogHeader className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 sm:px-5 sm:py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <Receipt className="h-3.5 w-3.5" />
                Nova despesa
              </div>
              <DialogTitle className="text-left text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
                {t('expensesPage.registerExpense')}
              </DialogTitle>
              <DialogDescription className="max-w-3xl text-left text-xs text-slate-500 sm:text-sm">
                Layout operativo ampliado, com mais espaço para dados, artigos, pagamentos e validação rápida.
              </DialogDescription>
            </div>
            <div className="hidden rounded-xl border border-slate-200 bg-white px-3 py-2 text-right sm:block">
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Estado</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">Registada / Paga</p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6 xl:px-8 2xl:px-10">
          <div className="grid gap-4 xl:grid-cols-[1.08fr_0.92fr] xl:gap-5">
            {showStep(0) && (
              <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4" data-testid="expense-create-basics-section">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Dados base</p>
                    <h3 className="mt-1 text-base font-semibold text-slate-950">Montante e descrição</h3>
                  </div>
                  <div className="rounded-2xl bg-emerald-50 px-3 py-2 text-right">
                    <p className="text-[11px] uppercase tracking-wide text-emerald-700">Valor</p>
                    <p className="text-base font-semibold text-emerald-700">{formData.amount || '0.00'} MTn</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="description">Descrição *</Label>
                    <Input id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Ex: Pagamento de electricidade - Janeiro" required data-testid="expense-description-input" className="h-10 rounded-xl border-slate-300 bg-white" />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="amount">Valor (MZN) *</Label>
                      <Input id="amount" type="number" step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" required disabled={formData.items.length > 0} data-testid="expense-amount-input" className="h-10 rounded-xl border-slate-300 bg-white text-sm font-semibold disabled:cursor-not-allowed disabled:bg-slate-100" />
                      {formData.items.length > 0 && <p className="text-xs text-muted-foreground" data-testid="expense-amount-derived-note">Valor calculado automaticamente pela soma dos artigos.</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Data da Despesa *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="outline" data-testid="expense-date-button" className={cn('h-10 w-full justify-start rounded-xl border-slate-300 bg-white text-left font-normal', !formData.expense_date && 'text-muted-foreground')}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.expense_date ? format(formData.expense_date, 'PPP', { locale: pt }) : 'Selecionar data'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={formData.expense_date} onSelect={(date) => setFormData({ ...formData, expense_date: date || new Date() })} initialFocus /></PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Clock className="h-4 w-4 text-amber-500" /> Data/Hora do Registo <span className="text-xs text-muted-foreground">(opcional)</span></Label>
                    <Input type="datetime-local" value={formData.registered_at} onChange={(e) => setFormData({ ...formData, registered_at: e.target.value })} max={new Date().toISOString().slice(0, 16)} data-testid="expense-registered-at-input" className="h-10 rounded-xl border-amber-200 bg-white focus:border-amber-400" />
                    <p className="text-xs text-muted-foreground">Use este campo se a despesa foi efectuada numa data/hora diferente.</p>
                  </div>
                </div>
              </section>
            )}

            {showStep(1) && (
              <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4" data-testid="expense-create-classification-section">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Classificação</p>
                <h3 className="mt-1 text-base font-semibold text-slate-950">Categoria e rastreio</h3>
                <div className="mt-4 space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Categoria *</Label>
                      <Select value={formData.expense_category_id} onValueChange={(value) => {
                        setCreateSubcategories([]);
                        setFormData({ ...formData, expense_category_id: value, expense_subcategory_id: '' });
                      }}>
                        <SelectTrigger data-testid="expense-category-select" className="h-10 rounded-xl border-slate-300 bg-white"><SelectValue placeholder="Selecionar categoria" /></SelectTrigger>
                        <SelectContent>{topLevelCategories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.expense_code} · {cat.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Subcategoria {currentCreateSubcategories.length > 0 ? '*' : ''}</Label>
                      <Select key={`create-subcategory-${formData.expense_category_id || 'none'}`} value={formData.expense_subcategory_id} onValueChange={(value) => setFormData({ ...formData, expense_subcategory_id: value === 'none' ? '' : value })} disabled={!formData.expense_category_id}>
                        <SelectTrigger data-testid="expense-subcategory-select" className="h-10 rounded-xl border-slate-300 bg-white"><SelectValue placeholder={currentCreateSubcategories.length > 0 ? 'Selecionar subcategoria' : 'Opcional'} /></SelectTrigger>
                        <SelectContent>
                          {currentCreateSubcategories.length === 0 && <SelectItem value="none">Nenhuma</SelectItem>}
                          {currentCreateSubcategories.map(sub => <SelectItem key={sub.id} value={sub.id}>{sub.expense_code} · {sub.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-1">
                    <div className="space-y-2">
                      <Label>Localização *</Label>
                      <Select value={formData.location_id} onValueChange={(value) => setFormData({ ...formData, location_id: value })}>
                        <SelectTrigger data-testid="expense-location-select" className="h-10 rounded-xl border-slate-300 bg-white"><SelectValue placeholder="Onde foi a despesa?" /></SelectTrigger>
                        <SelectContent>{locations.map(loc => <SelectItem key={loc.id} value={loc.id}><div className="flex items-center gap-2"><MapPin className="h-4 w-4" />{loc.name}</div></SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {showStep(2) && (
              <ExpenseItemsEditor
                items={formData.items}
                onAddItem={addCreateExpenseItem}
                onRemoveItem={removeCreateExpenseItem}
                onItemChange={updateCreateExpenseItem}
                totalAmount={createItemsSummary.total.toFixed(2)}
                testIdPrefix="expense-create-items-editor"
                className="xl:col-span-2"
                inventoryEnabled={isCreatePgtInventoryEligible}
                products={inventoryProducts}
                locations={locations}
              />
            )}

            {showStep(3) && (
              <>
                <div className="xl:col-span-2">
                  <ExpenseSplitPaymentsEditor
                    payments={formData.payments || []}
                    paymentMethods={paymentMethods}
                    totalAmount={formData.amount}
                    onAddPayment={addCreateExpensePayment}
                    onRemovePayment={removeCreateExpensePayment}
                    onPaymentChange={updateCreateExpensePayment}
                    title="Múltiplas formas de pagamento"
                    description="Se houver liquidação imediata, divida o valor por vários métodos. Se deixar vazio, a despesa será apenas registada."
                    testIdPrefix="expense-create-payments"
                  />
                </div>

                <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4 xl:col-span-2" data-testid="expense-create-counterparty-section">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Pagamento e destinatário</p>
                  <h3 className="mt-1 text-base font-semibold text-slate-950">Fornecedor ou beneficiário</h3>
                  <div className="mt-4 grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
                    <div className="space-y-2">
                      <Label htmlFor="reference_number">Nº Factura/Recibo</Label>
                      <Input id="reference_number" value={formData.reference_number} onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} placeholder="FT2024/001" data-testid="expense-reference-input" className="h-10 rounded-xl border-slate-300 bg-white" />
                    </div>
                    <div className="space-y-2">
                      <Label>Fornecedor/Beneficiário</Label>
                      <div className="grid gap-2 md:grid-cols-[160px_minmax(0,1fr)_auto]">
                        <Select value={formData.counterparty_type} onValueChange={(value) => {
                          setCreateCounterpartyError('');
                          setFormData({ ...formData, counterparty_type: value, supplier_id: '', beneficiary_employee_id: '', supplier_name: '' });
                        }}>
                          <SelectTrigger data-testid="expense-counterparty-type-select" className="h-10 rounded-xl border-slate-300 bg-white"><SelectValue placeholder="Tipo" /></SelectTrigger>
                          <SelectContent><SelectItem value="supplier">Fornecedor</SelectItem><SelectItem value="employee">Colaborador</SelectItem></SelectContent>
                        </Select>
                        {formData.counterparty_type === 'supplier' ? (
                          <Select value={formData.supplier_id} onValueChange={(value) => {
                            setCreateCounterpartyError('');
                            setFormData({ ...formData, supplier_id: value, beneficiary_employee_id: '', supplier_name: suppliers.find(item => item.id === value)?.name || '' });
                          }}>
                            <SelectTrigger data-testid="expense-supplier-select" className="h-10 rounded-xl border-slate-300 bg-white"><SelectValue placeholder="Seleccionar fornecedor" /></SelectTrigger>
                            <SelectContent>{suppliers.map(item => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <Select value={formData.beneficiary_employee_id} onValueChange={(value) => {
                            setCreateCounterpartyError('');
                            setFormData({ ...formData, beneficiary_employee_id: value, supplier_id: '', supplier_name: employees.find(item => item.id === value)?.name || '' });
                          }}>
                            <SelectTrigger data-testid="expense-beneficiary-select" className="h-10 rounded-xl border-slate-300 bg-white"><SelectValue placeholder="Seleccionar colaborador" /></SelectTrigger>
                            <SelectContent>{employees.map(item => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                        <Button type="button" variant="outline" data-testid="expense-quick-create-counterparty-button" className="h-10 rounded-xl" onClick={() => {
                          setQuickCreateTarget('create');
                          setQuickCreateType(formData.counterparty_type || 'supplier');
                          setQuickCreateForm({ name: '', phone: '', email: '', employee_number: '', role: 'staff', can_sell: true, contact_person: '', tax_id: '', address: '', city: 'Maputo', payment_terms: '30 dias', notes: '' });
                          setQuickCreateDialogOpen(true);
                        }}>
                          {formData.counterparty_type === 'supplier' ? 'Novo fornecedor' : 'Novo colaborador'}
                        </Button>
                      </div>
                      {createCounterpartyError && (
                        <p className="text-sm font-medium text-rose-600" data-testid="expense-counterparty-validation-message">
                          {createCounterpartyError}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4 xl:col-span-2" data-testid="expense-create-evidence-section">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Contexto operacional</p>
                  <h3 className="mt-1 text-base font-semibold text-slate-950">Notas da despesa</h3>
                  <div className="mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notas</Label>
                      <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Observações adicionais..." rows={6} data-testid="expense-notes-input" className="min-h-[160px] rounded-xl border-slate-300 bg-white" />
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 z-20 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-6 sm:py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              <strong>Fluxo novo:</strong> a despesa fica <Badge variant="outline">Registada</Badge> sem aprovação manual e passa para <Badge variant="outline">Paga</Badge> quando a soma dos pagamentos fecha o valor total.
              <div className="mt-2 text-xs text-blue-700" data-testid="expense-create-payments-preview">
                Pagamentos preparados: {formatCurrency(createPaymentsSummary.total)} · Saldo previsto: {formatCurrency(Math.max(Number(formData.amount || 0) - createPaymentsSummary.total, 0))}
              </div>
            </div>
            <div className="flex gap-2 sm:flex-shrink-0">
              <Button type="button" variant="outline" className="h-10 rounded-xl px-4" onClick={() => handleCreateDialogOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-expense-button" className="h-10 rounded-xl bg-slate-900 px-5 text-white hover:bg-slate-800">
                Registar Despesa
              </Button>
            </div>
          </div>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-3 p-4 lg:p-5" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{fontFamily: 'Manrope'}}>
            {t('expensesPage.title')}
          </h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Leitura operacional compacta do período activo
          </p>
        </div>
        
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={handleCreateDialogOpenChange}>
            <DialogTrigger asChild>
              <Button data-testid="add-expense-button">
                <Plus className="mr-2 h-4 w-4" />
                {t('expensesPage.newExpense')}
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[min(100vw-1rem,1560px)] max-w-[1560px] max-h-[96vh] rounded-[22px] border border-slate-200 p-0 overflow-hidden" data-testid="add-expense-centered-dialog">
              {renderCreateExpenseForm()}
            </DialogContent>
          </Dialog>
        )}
      </div>

      <ExpensesFiltersDeck
        summary={summary}
        filteredExpensesCount={filteredExpenses.length}
        activeStatusLabel={activeStatusInsight.label}
        visibleExpenseAmount={visibleExpenseAmount}
        activeExpenseFilterChips={activeExpenseFilterChips}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        periodValue={filters.period || 'all'}
        onPeriodChange={(value) => setFilters({ ...filters, period: value })}
        periodLabels={EXPENSE_PERIOD_LABELS}
        customStart={filters.custom_start}
        customEnd={filters.custom_end}
        onCustomStartChange={(value) => setFilters({ ...filters, custom_start: value || null })}
        onCustomEndChange={(value) => setFilters({ ...filters, custom_end: value || null })}
        selectedLocationIds={multiExpenseFilters.locations}
        onLocationChange={(value) => setMultiExpenseFilters((current) => ({ ...current, locations: value ? [value] : [] }))}
        locations={locations}
        selectedStatusIds={multiExpenseFilters.statuses}
        onStatusChange={(value) => setMultiExpenseFilters((current) => ({ ...current, statuses: value ? [value] : [] }))}
        statusOptions={EXPENSES_STATUS_OPTIONS}
        statusLabels={EXPENSES_STATUS_LABELS}
        selectedCategoryIds={multiExpenseFilters.categories}
        onCategoryChange={(value) => setMultiExpenseFilters((current) => ({ ...current, categories: value ? [value] : [], subcategories: [] }))}
        categoryFilterOptions={categoryFilterOptions}
        selectedSubcategoryIds={multiExpenseFilters.subcategories}
        onSubcategoryChange={(value) => setMultiExpenseFilters((current) => ({ ...current, subcategories: value ? [value] : [] }))}
        quickFilterSubcategories={quickFilterSubcategories}
        selectedPaymentAccountIds={multiExpenseFilters.paymentAccounts}
        onPaymentAccountChange={(value) => setMultiExpenseFilters((current) => ({ ...current, paymentAccounts: value ? [value] : [] }))}
        paymentAccounts={paymentAccounts}
        onResetFilters={handleResetExpenseFilters}
        actionsSlot={(
          <>
            <div className="flex shrink-0 items-center gap-1" data-testid="expenses-export-panel">
              <ExportButton
                module="expenses"
                label="Exp."
                filters={{
                  period: filters.period,
                  start_date: filters.custom_start,
                  end_date: filters.custom_end,
                  location_id: multiExpenseFilters.locations.length ? serializeMultiValueParam(multiExpenseFilters.locations) : null,
                  status: multiExpenseFilters.statuses.length ? serializeMultiValueParam(multiExpenseFilters.statuses) : (activeTab !== 'all' ? activeTab : null),
                  category_id: multiExpenseFilters.categories.length ? serializeMultiValueParam(multiExpenseFilters.categories) : null,
                  subcategory_id: multiExpenseFilters.subcategories.length ? serializeMultiValueParam(multiExpenseFilters.subcategories) : null,
                  payment_account_id: multiExpenseFilters.paymentAccounts.length ? serializeMultiValueParam(multiExpenseFilters.paymentAccounts) : null,
                  search: searchQuery.trim() || null,
                }}
              />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" className="h-8 w-8 shrink-0 rounded-lg border-slate-200 bg-white px-0 text-[10px]" data-testid="expenses-more-menu-button" title="Mais opções">
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[320px] space-y-3 p-3" data-testid="expenses-more-menu-content">
                {pgtAnalytics && (
                  <>
                    <div className="space-y-2" data-testid="expenses-more-menu-pgt-summary">
                      <DropdownMenuLabel className="px-0 pb-0 text-xs uppercase tracking-[0.18em] text-slate-500">Resumo PGT</DropdownMenuLabel>
                      <div className="flex flex-wrap gap-1.5 text-xs">
                        <Badge variant="outline" data-testid="expense-pgt-category-top-chip">Top categoria: {pgtAnalytics.by_category?.[0]?.category_code || '—'}</Badge>
                        <Badge variant="outline" data-testid="expense-pgt-subcategory-top-chip">Top subcategoria: {pgtAnalytics.by_subcategory?.[0]?.subcategory_code || '—'}</Badge>
                      </div>
                    </div>
                  </>
                )}

                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="space-y-2" data-testid="expenses-more-menu-admin-tools">
                      <DropdownMenuLabel className="px-0 pb-0 text-xs uppercase tracking-[0.18em] text-slate-500">Admin</DropdownMenuLabel>
                      <Button type="button" variant="outline" className="h-9 w-full justify-start rounded-lg border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-100" onClick={handlePreviewRenumberCodes} disabled={renumberLoading} data-testid="expenses-renumber-preview-button">
                        Prévia de renumeração
                      </Button>
                      <Button type="button" className="h-9 w-full justify-start rounded-lg bg-indigo-900 text-white hover:bg-indigo-800" onClick={() => setRenumberDialogOpen(true)} disabled={renumberLoading} data-testid="expenses-renumber-run-button">
                        Executar renumeração
                      </Button>
                      {renumberResult && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="expenses-renumber-preview-summary">
                          Total {renumberResult.total_expenses || 0} · A alterar {renumberResult.changed_expenses || 0}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      />

      <ExpensesItemsRollupCard
        items={expenseItemRollup}
        collapsed={expenseRollupCollapsed}
        onToggle={() => setExpenseRollupCollapsed((prev) => !prev)}
      />

      <div className="flex flex-wrap items-center gap-2" data-testid="expenses-workspace-switcher">
        <Button
          type="button"
          variant={activeWorkspaceTab === 'expenses' ? 'default' : 'outline'}
          className="h-9 rounded-full px-4 text-xs"
          onClick={() => setActiveWorkspaceTab('expenses')}
          data-testid="expenses-workspace-expenses-button"
        >
          Lista de despesas
        </Button>
        <Button
          type="button"
          variant={activeWorkspaceTab === 'operational' ? 'default' : 'outline'}
          className="h-9 rounded-full px-4 text-xs"
          onClick={() => setActiveWorkspaceTab('operational')}
          data-testid="expenses-workspace-pgt01-button"
        >
          PGT01 operacional
        </Button>
        <Badge variant="outline" className="rounded-full px-3 py-1 text-[11px]" data-testid="expenses-workspace-bridge-badge">
          Ponte Despesas ↔ Inventário
        </Badge>
      </div>

      {/* Status Tabs */}
      {activeWorkspaceTab === 'expenses' ? (
      <Tabs value={activeTab} onValueChange={handleStatusTabChange} className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1.5 bg-transparent p-0" data-testid="expense-status-tabs">
          {statusInsights.map((tab) => {
            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                data-testid={tab.value === 'all' ? 'tab-all' : tab.value === 'pending_approval' ? 'tab-pending' : `tab-${tab.value}`}
                className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm transition-colors hover:border-slate-300 data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              >
                <span className="font-medium">{tab.label}</span>
                <Badge variant="secondary" className="ml-2 rounded-full px-2 py-0 text-[11px]" data-testid={`expense-status-count-${tab.value}`}>
                  {tab.count}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="expenses-list-shell">
            <CardHeader className="border-b border-slate-200 bg-slate-50/70 py-3">
              <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base text-slate-950">Lista operacional de despesas</CardTitle>
                  <CardDescription data-testid="expenses-list-summary-text" className="text-xs">
                    {activeStatusInsight.label} · {sortedExpenses.length} registos · {formatCurrency(visibleExpenseAmount)}
                  </CardDescription>
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="expenses-bulk-toolbar">
                    {selectedExpenseIds.length > 0 && (
                      <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-2 py-0 text-[10px] text-slate-700" data-testid="expenses-selection-badge">
                        {selectedExpenseIds.length} sel.
                      </Badge>
                    )}
                    <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]" onClick={() => toggleAllVisibleExpenses(!allVisibleExpensesSelected)} data-testid="expenses-select-all-visible-button">
                      {allVisibleExpensesSelected ? 'Limpar visíveis' : 'Seleccionar visíveis'}
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]" disabled={!selectedExpenseIds.length} onClick={handleBulkNormalizeExpenses} data-testid="expenses-bulk-normalize-button">
                      Aprovar / normalizar legacy
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]" disabled={!selectedExpenseIds.length} onClick={handleExportSelectedExpenses} data-testid="expenses-bulk-export-button">
                      Exportar seleccionadas
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5" data-testid="expenses-list-sort-controls">
                  <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[10px] text-slate-700" data-testid="expenses-sort-current-badge">
                    Ord.: {expenseSortLabel}
                  </Badge>
                  <Select value={expenseSortField} onValueChange={setExpenseSortField}>
                    <SelectTrigger className="h-8 w-[154px] rounded-xl border-slate-300 bg-white text-[10px]" data-testid="expenses-sort-field-select">
                      <SelectValue placeholder="Ordenar por" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_SORT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant={expenseSortDirection === 'asc' ? 'default' : 'outline'}
                    className="h-8 rounded-xl px-2 text-[10px]"
                    onClick={() => setExpenseSortDirection('asc')}
                    data-testid="expenses-sort-asc-button"
                  >
                    A-Z
                  </Button>
                  <Button
                    type="button"
                    variant={expenseSortDirection === 'desc' ? 'default' : 'outline'}
                    className="h-8 rounded-xl px-2 text-[10px]"
                    onClick={() => setExpenseSortDirection('desc')}
                    data-testid="expenses-sort-desc-button"
                  >
                    Z-A
                  </Button>
                </div>
              </div>
              {activeTab === 'cancelled' && (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="expenses-cancelled-banner">
                  Nesta tab ficam apenas despesas canceladas. {isAdmin ? 'Pode eliminá-las permanentemente pelo menu de acções.' : 'A eliminação permanente fica reservada a administradores.'}
                </div>
              )}
            </CardHeader>

            <CardContent className="pt-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : filteredExpenses.length === 0 ? (
                <div className="text-center py-12" data-testid="expenses-empty-state">
                  <Receipt className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    {activeTab === 'cancelled'
                      ? 'Sem despesas canceladas neste momento'
                      : searchQuery || multiExpenseFilters.categories.length || multiExpenseFilters.subcategories.length
                      ? 'Nenhuma despesa encontrada com os filtros PGT actuais' 
                      : 'Sem despesas registadas'}
                  </p>
                  {canCreate && (
                    <Button variant="link" onClick={() => setDialogOpen(true)} className="mt-2" data-testid="expenses-empty-create-button">
                      Criar primeira despesa
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className="hidden lg:block" data-testid="expenses-list-table-wrapper">
                    <div className="overflow-x-auto" ref={expenseTableWrapperRef}>
                      <table className={`min-w-full ${EXPENSES_TABLE_CLASS}`} data-testid="expenses-list-table">
                        <colgroup>
                          {expenseColumnWidths.map((width, index) => <col key={`expense-column-${index}`} style={{ width: `${width}%` }} />)}
                        </colgroup>
                        <thead className="border-b border-slate-200 bg-slate-50 text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                          <tr>
                            <th className="relative px-2 py-1.5">
                              <Checkbox checked={allVisibleExpensesSelected} onCheckedChange={(checked) => toggleAllVisibleExpenses(Boolean(checked))} data-testid="expenses-select-all-checkbox" />
                              <button type="button" className="absolute inset-y-1 right-0 w-2 cursor-col-resize" onMouseDown={(event) => startExpenseColumnResize(0, event)} data-testid="expenses-column-splitter-0"><span className="mx-auto block h-full w-px bg-slate-300" /></button>
                            </th>
                            <th className="relative px-2 py-1.5"><button type="button" className="inline-flex items-center gap-1" onClick={() => handleExpenseSort('document')} data-testid="expenses-sort-document-header">Documento {renderExpenseSortIndicator('document')}</button><button type="button" className="absolute inset-y-1 right-0 w-2 cursor-col-resize" onMouseDown={(event) => startExpenseColumnResize(1, event)} data-testid="expenses-column-splitter-1"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                            <th className="relative px-2 py-1.5"><button type="button" className="inline-flex items-center gap-1" onClick={() => handleExpenseSort('description')} data-testid="expenses-sort-description-header">Descrição {renderExpenseSortIndicator('description')}</button><button type="button" className="absolute inset-y-1 right-0 w-2 cursor-col-resize" onMouseDown={(event) => startExpenseColumnResize(2, event)} data-testid="expenses-column-splitter-2"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                            <th className="relative px-2 py-1.5"><button type="button" className="inline-flex items-center gap-1" onClick={() => handleExpenseSort('expense_date')} data-testid="expenses-sort-date-header">Data {renderExpenseSortIndicator('expense_date')}</button><button type="button" className="absolute inset-y-1 right-0 w-2 cursor-col-resize" onMouseDown={(event) => startExpenseColumnResize(3, event)} data-testid="expenses-column-splitter-3"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                            <th className="relative px-2 py-1.5"><button type="button" className="inline-flex items-center gap-1" onClick={() => handleExpenseSort('pgt')} data-testid="expenses-sort-pgt-header">PGT {renderExpenseSortIndicator('pgt')}</button><button type="button" className="absolute inset-y-1 right-0 w-2 cursor-col-resize" onMouseDown={(event) => startExpenseColumnResize(4, event)} data-testid="expenses-column-splitter-4"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                            <th className="relative px-2 py-1.5"><button type="button" className="inline-flex items-center gap-1" onClick={() => handleExpenseSort('account_location')} data-testid="expenses-sort-account-header">Conta {renderExpenseSortIndicator('account_location')}</button><button type="button" className="absolute inset-y-1 right-0 w-2 cursor-col-resize" onMouseDown={(event) => startExpenseColumnResize(5, event)} data-testid="expenses-column-splitter-5"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                            <th className="relative px-2 py-1.5"><span>Loja</span><button type="button" className="absolute inset-y-1 right-0 w-2 cursor-col-resize" onMouseDown={(event) => startExpenseColumnResize(6, event)} data-testid="expenses-column-splitter-6"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                            <th className="relative px-2 py-1.5"><span>Pagamento</span><button type="button" className="absolute inset-y-1 right-0 w-2 cursor-col-resize" onMouseDown={(event) => startExpenseColumnResize(7, event)} data-testid="expenses-column-splitter-7"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                            <th className="relative px-2 py-1.5 text-center"><button type="button" className="inline-flex items-center gap-1" onClick={() => handleExpenseSort('items')} data-testid="expenses-sort-items-header">Artigos {renderExpenseSortIndicator('items')}</button><button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startExpenseColumnResize(8, event)} data-testid="expenses-column-splitter-8"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                            <th className="relative px-2 py-1.5"><span>Estado</span><button type="button" className="absolute inset-y-0 right-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startExpenseColumnResize(9, event)} data-testid="expenses-column-splitter-9"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                            <th className="relative px-2 py-1.5 text-right"><button type="button" className="inline-flex items-center gap-1 justify-end" onClick={() => handleExpenseSort('amount')} data-testid="expenses-sort-amount-header">Valor {renderExpenseSortIndicator('amount')}</button></th>
                            <th className="relative px-2 py-1.5 text-right">Acções<button type="button" className="absolute inset-y-0 left-0 w-3 cursor-col-resize hover:bg-slate-100/70" onMouseDown={(event) => startExpenseColumnResize(10, event)} data-testid="expenses-column-splitter-10"><span className="mx-auto block h-full w-px bg-slate-300" /></button></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedExpenses.map((expense) => {
                            const statusConfig = STATUS_CONFIG[expense.status] || STATUS_CONFIG.draft;
                            const StatusIcon = statusConfig.icon;
                            return (
                              <tr key={expense.id} className={cn('border-b border-slate-100 align-top hover:bg-slate-50/70', selectedExpenseIds.includes(expense.id) && 'bg-slate-100/80')} data-testid={`expense-table-row-${expense.id}`}>
                                <td className="px-2 py-1.5 align-top">
                                  <Checkbox checked={selectedExpenseIds.includes(expense.id)} onCheckedChange={(checked) => toggleExpenseSelection(expense.id, Boolean(checked))} data-testid={`expense-select-checkbox-${expense.id}`} />
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="space-y-0.5">
                                    <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700" data-testid={`expense-code-${expense.id}`}>{expense.expense_code}</code>
                                    <p className="text-[10px] text-slate-500" data-testid={`expense-reference-${expense.id}`}>{expense.reference_number || 'Sem ref.'}</p>
                                  </div>
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="max-w-[260px] space-y-0.5">
                                    <p className="line-clamp-1 font-semibold text-[11px] text-slate-950" data-testid={`expense-description-${expense.id}`}>{expense.description}</p>
                                    <p className="text-[10px] text-muted-foreground" data-testid={`expense-counterparty-${expense.id}`}>
                                      {expense.supplier_name || 'Sem responsável'}
                                    </p>
                                  </div>
                                </td>
                                <td className="px-2 py-1.5 text-[10px] text-slate-600">
                                  <span data-testid={`expense-date-${expense.id}`}>{formatDate(expense.expense_date)}</span>
                                </td>
                                <td className="px-2 py-1.5">
                                  <p className="text-[10px] text-slate-600" data-testid={`expense-category-${expense.id}`}>{expense.expense_category_name || 'Sem categoria'}</p>
                                </td>
                                <td className="px-2 py-1.5 text-[10px] text-slate-600">
                                  <span data-testid={`expense-account-${expense.id}`}>{getExpenseAccountLabel(expense)}</span>
                                </td>
                                <td className="px-2 py-1.5 text-[10px] text-slate-600">
                                  <span data-testid={`expense-location-${expense.id}`}>{expense.location_name || 'Sem local'}</span>
                                </td>
                                <td className="px-2 py-1.5 text-[10px] text-slate-600">
                                  <span data-testid={`expense-payment-methods-${expense.id}`}>{getExpensePaymentMethodsLabel(expense)}</span>
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  {expense.items_count > 0 ? (
                                    <div className="text-[10px] text-slate-600" data-testid={`expense-items-count-${expense.id}`}>{expense.items_count} art.</div>
                                  ) : (
                                    <span className="text-[10px] text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="text-[10px]">
                                    <Badge variant="outline" className={cn('px-1 py-0 text-[9px]', statusConfig.color)} data-testid={`expense-status-badge-${expense.id}`}>
                                      <StatusIcon className="mr-1 h-2.5 w-2.5" />{statusConfig.label}
                                    </Badge>
                                    {expense.inventory_linked_items_count > 0 && (
                                      <Badge variant="outline" className="mt-1 px-1 py-0 text-[9px]" data-testid={`expense-inventory-status-badge-${expense.id}`}>
                                        {INVENTORY_SYNC_STATUS_LABELS[expense.inventory_sync_status] || 'Inventário'}
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-1.5 text-right">
                                  <p className="font-semibold text-[11px] text-emerald-700" data-testid={`expense-amount-${expense.id}`}>{formatCurrency(expense.amount)}</p>
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="flex justify-end">{renderExpenseActionButtons(expense)}</div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="space-y-2 lg:hidden" data-testid="expenses-list-grid">
                    {sortedExpenses.map((expense) => {
                      const statusConfig = STATUS_CONFIG[expense.status] || STATUS_CONFIG.draft;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <article key={expense.id} className={cn('rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm', selectedExpenseIds.includes(expense.id) && 'border-slate-900')} data-testid={`expense-list-card-${expense.id}`}>
                          <div className="flex flex-col gap-2">
                            <div className="flex justify-end">
                              <Checkbox checked={selectedExpenseIds.includes(expense.id)} onCheckedChange={(checked) => toggleExpenseSelection(expense.id, Boolean(checked))} data-testid={`expense-card-select-checkbox-${expense.id}`} />
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <code className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700" data-testid={`expense-code-${expense.id}`}>{expense.expense_code}</code>
                              <Badge variant="outline" className={cn('text-[11px]', statusConfig.color)} data-testid={`expense-status-badge-${expense.id}`}><StatusIcon className="mr-1 h-3 w-3" />{statusConfig.label}</Badge>
                              {expense.inventory_linked_items_count > 0 && <Badge variant="outline" data-testid={`expense-inventory-status-badge-${expense.id}`}>{INVENTORY_SYNC_STATUS_LABELS[expense.inventory_sync_status] || 'Inventário'}</Badge>}
                              <span className="text-[11px] text-muted-foreground" data-testid={`expense-date-${expense.id}`}>{formatDate(expense.expense_date)}</span>
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-slate-950" data-testid={`expense-description-${expense.id}`}>{expense.description}</h3>
                              <p className="text-[11px] text-muted-foreground" data-testid={`expense-counterparty-${expense.id}`}>{expense.supplier_name || 'Sem responsável'}{expense.reference_number ? ` · Ref: ${expense.reference_number}` : ''}</p>
                            </div>
                            <div className="flex flex-wrap gap-1.5 text-[11px]">
                              <Badge variant="outline" data-testid={`expense-location-${expense.id}`}>{expense.location_name || 'Sem local'}</Badge>
                              <Badge variant="outline" data-testid={`expense-account-${expense.id}`}>{getExpenseAccountLabel(expense)}</Badge>
                              <Badge variant="outline" data-testid={`expense-payment-methods-${expense.id}`}>{getExpensePaymentMethodsLabel(expense)}</Badge>
                              {expense.items_count > 0 && <Badge variant="outline" data-testid={`expense-items-count-${expense.id}`}>{expense.items_count} artigo(s) · saldo {expense.remaining_items_quantity || 0}</Badge>}
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <p className="text-base font-semibold text-emerald-700" data-testid={`expense-amount-${expense.id}`}>{formatCurrency(expense.amount)}</p>
                                <p className="text-[11px] text-slate-500" data-testid={`expense-settlement-${expense.id}`}>{getExpenseSettlementLabel(expense)}</p>
                              </div>
                              {renderExpenseActionButtons(expense)}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      ) : (
        <div className="space-y-4" data-testid="pgt01-operational-workspace">
          <Card className="border-slate-200 shadow-sm" data-testid="pgt01-operational-summary-card">
            <CardHeader className="border-b border-slate-200 bg-slate-50/70 py-3">
              <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <CardTitle className="text-base text-slate-950">Histórico e relatórios operacionais PGT01</CardTitle>
                  <CardDescription className="text-xs" data-testid="pgt01-operational-summary-description">
                    Auditoria das recepções, anulações e estado actual do stock originado por despesas PGT01.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  {pgt01OperationalSummaryCards.map((item) => (
                    <div key={item.key} className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-right shadow-sm" data-testid={`pgt01-summary-card-${item.key}`}>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{item.label}</p>
                      <p className={`mt-1 text-lg font-semibold ${item.accent}`}>{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="grid gap-3 py-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid="pgt01-summary-requested-quantity-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Qtd. pedida</p>
                <p className="mt-1 text-xl font-semibold text-slate-900">{Number(pgt01OperationalReport?.summary?.total_requested_quantity || 0).toLocaleString('pt-PT')}</p>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3" data-testid="pgt01-summary-received-quantity-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-700">Qtd. recebida</p>
                <p className="mt-1 text-xl font-semibold text-emerald-700">{Number(pgt01OperationalReport?.summary?.total_received_quantity || 0).toLocaleString('pt-PT')}</p>
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3" data-testid="pgt01-summary-pending-items-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-amber-700">Itens pendentes</p>
                <p className="mt-1 text-xl font-semibold text-amber-700">{pgt01OperationalReport?.summary?.pending_items || 0}</p>
              </div>
              <div className="rounded-2xl border border-red-200 bg-red-50 px-3 py-3" data-testid="pgt01-summary-reversal-blocked-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-red-700">Reversões bloqueadas</p>
                <p className="mt-1 text-xl font-semibold text-red-700">{pgt01OperationalReport?.summary?.reversal_blocked_expenses || 0}</p>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
            <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="pgt01-operational-history-card">
              <CardHeader className="border-b border-slate-200 bg-slate-50/70 py-3">
                <CardTitle className="text-base text-slate-950">Histórico de recepções e anulações</CardTitle>
                <CardDescription className="text-xs">Leitura cronológica para auditoria operacional do PGT01.</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {!pgt01OperationalReport?.receipt_history?.length ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500" data-testid="pgt01-operational-history-empty">
                    Ainda não existem eventos operacionais PGT01 dentro dos filtros actuais.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm" data-testid="pgt01-operational-history-table">
                      <thead className="border-b border-slate-200 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500">
                        <tr>
                          <th className="py-2 pr-4">Evento</th>
                          <th className="py-2 pr-4">Despesa</th>
                          <th className="py-2 pr-4">Armazéns</th>
                          <th className="py-2 pr-4">Estado</th>
                          <th className="py-2 pr-4">Quando</th>
                          <th className="py-2">Quem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pgt01OperationalReport.receipt_history.map((row) => (
                          <tr key={row.event_id} className="border-b border-slate-100 align-top">
                            <td className="py-2 pr-4">
                              <Badge variant="outline" data-testid={`pgt01-history-event-${row.event_id}`}>{row.event_label}</Badge>
                            </td>
                            <td className="py-2 pr-4">
                              <div className="space-y-0.5">
                                <p className="font-semibold text-slate-950">{row.expense_code}</p>
                                <p className="text-xs text-slate-500">{row.description}</p>
                              </div>
                            </td>
                            <td className="py-2 pr-4 text-xs text-slate-600">{(row.warehouse_names || []).join(' · ') || 'Sem armazém'}</td>
                            <td className="py-2 pr-4 text-xs text-slate-600">{PGT01_OPERATIONAL_STATUS_LABELS[row.inventory_sync_status] || row.inventory_sync_status}</td>
                            <td className="py-2 pr-4 text-xs text-slate-600">{row.timestamp ? formatDateTime(row.timestamp) : '—'}</td>
                            <td className="py-2 text-xs text-slate-600">{row.actor_name || 'Sistema'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="pgt01-operational-category-card">
              <CardHeader className="border-b border-slate-200 bg-slate-50/70 py-3">
                <CardTitle className="text-base text-slate-950">Relatório por categoria</CardTitle>
                <CardDescription className="text-xs">Quanto cada categoria PGT01 já gerou em entradas e pendências.</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm" data-testid="pgt01-operational-category-table">
                    <thead className="border-b border-slate-200 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      <tr>
                        <th className="py-2 pr-4">Categoria</th>
                        <th className="py-2 pr-4 text-right">Despesas</th>
                        <th className="py-2 pr-4 text-right">Itens</th>
                        <th className="py-2 pr-4 text-right">Pendentes</th>
                        <th className="py-2 text-right">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pgt01OperationalReport?.by_category || []).map((row) => (
                        <tr key={row.category_id || row.category_code} className="border-b border-slate-100">
                          <td className="py-2 pr-4">
                            <p className="font-semibold text-slate-950" data-testid={`pgt01-category-row-${row.category_code}`}>{row.category_code}</p>
                            <p className="text-xs text-slate-500">{row.category_name}</p>
                          </td>
                          <td className="py-2 pr-4 text-right text-slate-700">{row.expense_count}</td>
                          <td className="py-2 pr-4 text-right text-slate-700">{row.linked_items_count}</td>
                          <td className="py-2 pr-4 text-right text-amber-700">{row.pending_items_count}</td>
                          <td className="py-2 text-right font-semibold text-slate-900">{formatCurrency(row.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="pgt01-operational-expense-card">
              <CardHeader className="border-b border-slate-200 bg-slate-50/70 py-3">
                <CardTitle className="text-base text-slate-950">Relatório operacional por despesa</CardTitle>
                <CardDescription className="text-xs">Mostra a ligação de cada despesa PGT01 com o inventário e o último passo executado.</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm" data-testid="pgt01-operational-expense-table">
                    <thead className="border-b border-slate-200 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      <tr>
                        <th className="py-2 pr-4">Despesa</th>
                        <th className="py-2 pr-4">Categoria</th>
                        <th className="py-2 pr-4">Armazéns</th>
                        <th className="py-2 pr-4 text-right">Qtd.</th>
                        <th className="py-2 pr-4">Estado</th>
                        <th className="py-2 pr-4">Última acção</th>
                        <th className="py-2 text-right">Acções</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pgt01OperationalReport?.by_expense || []).map((row) => {
                        const expenseRow = expenses.find((item) => item.id === row.expense_id) || sortedExpenses.find((item) => item.id === row.expense_id);
                        return (
                          <tr key={row.expense_id} className="border-b border-slate-100 align-top">
                            <td className="py-2 pr-4">
                              <div className="space-y-0.5">
                                <p className="font-semibold text-slate-950" data-testid={`pgt01-expense-row-${row.expense_id}`}>{row.expense_code}</p>
                                <p className="text-xs text-slate-500">{row.description}</p>
                              </div>
                            </td>
                            <td className="py-2 pr-4 text-xs text-slate-600">{row.category_code} · {row.category_name}</td>
                            <td className="py-2 pr-4 text-xs text-slate-600">{(row.warehouses || []).map((warehouse) => warehouse.location_name).join(' · ') || 'Sem armazém'}</td>
                            <td className="py-2 pr-4 text-right text-slate-700">{Number(row.total_received_quantity || row.total_requested_quantity || 0).toLocaleString('pt-PT')}</td>
                            <td className="py-2 pr-4">
                              <Badge variant="outline" data-testid={`pgt01-expense-status-${row.expense_id}`}>{PGT01_OPERATIONAL_STATUS_LABELS[row.inventory_sync_status] || row.inventory_sync_status}</Badge>
                            </td>
                            <td className="py-2 pr-4 text-xs text-slate-600">{row.last_action_at ? `${formatDateTime(row.last_action_at)} · ${row.last_action_by_name || 'Sistema'}` : 'Aguardando recepção'}</td>
                            <td className="py-2 text-right">
                              <div className="flex flex-wrap justify-end gap-1.5">
                                <Button type="button" size="sm" variant="outline" className="h-8 rounded-xl px-3 text-[11px]" onClick={() => expenseRow && handleView(expenseRow)} data-testid={`pgt01-open-expense-${row.expense_id}`}>
                                  Ver despesa
                                </Button>
                                {expenseRow && canReceiveInventory && row.inventory_sync_status === 'pending_receipt' && (
                                  <Button type="button" size="sm" className="h-8 rounded-xl px-3 text-[11px]" onClick={() => handleConfirmInventoryReceipt(expenseRow)} data-testid={`pgt01-receive-expense-${row.expense_id}`}>
                                    Confirmar recepção
                                  </Button>
                                )}
                                {expenseRow && canReceiveInventory && ['received', 'reversal_blocked', 'pending_receipt'].includes(row.inventory_sync_status) && (
                                  <Button type="button" size="sm" variant="outline" className="h-8 rounded-xl px-3 text-[11px]" onClick={() => handleCancelInventorySync(expenseRow)} data-testid={`pgt01-cancel-expense-${row.expense_id}`}>
                                    Anular fluxo
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="pgt01-operational-warehouse-card">
              <CardHeader className="border-b border-slate-200 bg-slate-50/70 py-3">
                <CardTitle className="text-base text-slate-950">Relatório por armazém</CardTitle>
                <CardDescription className="text-xs">Resumo das entradas geradas por PGT01 em cada localização de stock.</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm" data-testid="pgt01-operational-warehouse-table">
                    <thead className="border-b border-slate-200 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500">
                      <tr>
                        <th className="py-2 pr-4">Armazém</th>
                        <th className="py-2 pr-4 text-right">Despesas</th>
                        <th className="py-2 pr-4 text-right">Itens</th>
                        <th className="py-2 pr-4 text-right">Pendentes</th>
                        <th className="py-2 text-right">Qtd. recebida</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pgt01OperationalReport?.by_warehouse || []).map((row) => (
                        <tr key={row.location_id || row.location_name} className="border-b border-slate-100">
                          <td className="py-2 pr-4">
                            <p className="font-semibold text-slate-950" data-testid={`pgt01-warehouse-row-${row.location_id || row.location_name}`}>{row.location_name}</p>
                          </td>
                          <td className="py-2 pr-4 text-right text-slate-700">{row.expense_count}</td>
                          <td className="py-2 pr-4 text-right text-slate-700">{row.linked_items_count}</td>
                          <td className="py-2 pr-4 text-right text-amber-700">{row.pending_items_count}</td>
                          <td className="py-2 text-right font-semibold text-emerald-700">{Number(row.total_received_quantity || 0).toLocaleString('pt-PT')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* View Expense Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="h-screen w-screen max-h-none max-w-none rounded-none border-0 p-0 overflow-hidden" data-testid="expense-view-fullscreen-dialog">
          <DialogHeader className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 sm:px-5 sm:py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  <Receipt className="h-3.5 w-3.5" />
                  Detalhes da despesa
                </div>
                <DialogTitle className="text-left text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">Detalhes da Despesa</DialogTitle>
                <DialogDescription className="max-w-2xl text-left text-xs text-slate-500 sm:text-sm">
                  Visualização operacional em ecrã inteiro, com foco nos artigos, classificação e valor da despesa.
                </DialogDescription>
              </div>
              {selectedExpense && (
                <div className="hidden rounded-xl border border-slate-200 bg-white px-3 py-2 text-right sm:block">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Estado</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">{STATUS_CONFIG[selectedExpense.status]?.label}</p>
                </div>
              )}
            </div>
          </DialogHeader>
          {selectedExpense && (
            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="rounded bg-white px-2 py-1 text-sm font-mono text-slate-700">{selectedExpense.expense_code}</code>
                    <Badge variant="outline" className={cn(STATUS_CONFIG[selectedExpense.status]?.color)}>{STATUS_CONFIG[selectedExpense.status]?.label}</Badge>
                  </div>
                  <p className="mt-3 text-sm font-medium text-slate-950">{selectedExpense.description}</p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-right">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-700">Valor</p>
                  <p className="mt-1 text-2xl font-semibold text-emerald-700">{formatCurrency(selectedExpense.amount)}</p>
                  <p className="mt-1 text-xs text-emerald-700" data-testid="expense-view-balance">Saldo: {formatCurrency(getExpensePaymentBalance(selectedExpense))}</p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-muted-foreground">Data</p>
                  <p className="mt-1 text-sm font-medium text-slate-950">{formatDate(selectedExpense.expense_date)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-muted-foreground">Categoria</p>
                  <p className="mt-1 text-sm font-medium text-slate-950">{selectedExpense.expense_category_name || '-'}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-muted-foreground">Subcategoria</p>
                  <p className="mt-1 text-sm font-medium text-slate-950">{selectedExpense.expense_subcategory_name || '—'}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-muted-foreground">Conta</p>
                  <p className="mt-1 text-sm font-medium text-slate-950">{getExpenseAccountLabel(selectedExpense)}</p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-muted-foreground">Localização</p>
                  <p className="mt-1 text-sm font-medium text-slate-950">{selectedExpense.location_name || '-'}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-muted-foreground">Fornecedor / Beneficiário</p>
                  <p className="mt-1 text-sm font-medium text-slate-950">{selectedExpense.supplier_name || '-'}</p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white px-4 py-4" data-testid="expense-view-payments-section">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Liquidação</p>
                    <h3 className="mt-1 text-base font-semibold text-slate-950">Formas de pagamento</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline" data-testid="expense-view-payments-total">Pago: {formatCurrency(selectedExpense.payments_total || 0)}</Badge>
                    <Badge variant="outline" data-testid="expense-view-payments-balance">Saldo: {formatCurrency(getExpensePaymentBalance(selectedExpense))}</Badge>
                    <Badge variant="outline" data-testid="expense-view-payments-methods">{getExpensePaymentMethodsLabel(selectedExpense)}</Badge>
                  </div>
                </div>

                {(selectedExpense.payments || []).length > 0 ? (
                  <div className="mt-4 space-y-3">
                    {(selectedExpense.payments || []).map((payment, index) => (
                      <div key={payment.id || `${selectedExpense.id}-payment-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`expense-view-payment-row-${index}`}>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="font-medium text-slate-950" data-testid={`expense-view-payment-method-${index}`}>{payment.payment_method_name || 'Método não identificado'}</p>
                            <p className="text-xs text-slate-500" data-testid={`expense-view-payment-reference-${index}`}>{payment.reference || payment.notes || 'Sem referência adicional'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-emerald-700" data-testid={`expense-view-payment-amount-${index}`}>{formatCurrency(payment.amount || 0)}</p>
                            <p className="text-xs text-slate-500" data-testid={`expense-view-payment-date-${index}`}>{payment.paid_at ? formatDateTime(payment.paid_at) : 'Sem data registada'}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-slate-500" data-testid="expense-view-payments-empty">Ainda não existem pagamentos registados para esta despesa.</p>
                )}
              </div>

              {selectedExpense.status === 'cancelled' && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3" data-testid="expense-view-cancelled-note">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-700">Cancelada</p>
                  <p className="mt-1 text-sm text-red-800">Esta despesa encontra-se na tab Canceladas e já pode ser eliminada permanentemente.</p>
                </div>
              )}

              {selectedExpense.items_count > 0 && (
                <div className="grid grid-cols-3 gap-4" data-testid="expense-view-items-summary-grid">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-xs text-muted-foreground">Artigos</p>
                    <p className="mt-1 text-lg font-semibold text-slate-950">{selectedExpense.items_count}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-xs text-muted-foreground">Qtd comprada</p>
                    <p className="mt-1 text-lg font-semibold text-slate-950">{selectedExpense.total_items_quantity || 0}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-xs text-muted-foreground">Saldo</p>
                    <p className="mt-1 text-lg font-semibold text-emerald-700">{selectedExpense.remaining_items_quantity || 0}</p>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="expense-view-inventory-summary">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Integração com inventário</p>
                    <p className="mt-1 text-sm text-slate-900">{INVENTORY_SYNC_STATUS_LABELS[selectedExpense.inventory_sync_status] || 'Sem integração activa'}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="outline" data-testid="expense-view-inventory-linked-count">Ligadas: {selectedExpense.inventory_linked_items_count || 0}</Badge>
                    <Badge variant="outline" data-testid="expense-view-inventory-pending-count">Pendentes: {selectedExpense.inventory_pending_receipt_items_count || 0}</Badge>
                    <Badge variant="outline" data-testid="expense-view-inventory-received-count">Recebidas: {selectedExpense.inventory_received_items_count || 0}</Badge>
                  </div>
                </div>
              </div>

              <ExpenseItemsTable items={selectedExpense.items || []} testIdPrefix="expense-view-items-table" />
              
              {selectedExpense.reference_number && (
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-muted-foreground">Nº Factura/Recibo</p>
                  <p className="mt-1 font-mono text-sm text-slate-950">{selectedExpense.reference_number}</p>
                </div>
              )}
              
              {selectedExpense.notes && (
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-xs text-muted-foreground">Notas</p>
                  <p className="mt-1 text-sm text-slate-700">{selectedExpense.notes}</p>
                </div>
              )}
              
              {/* Audit info */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Criado por:</span>
                  <span>{selectedExpense.created_by_name} em {formatDateTime(selectedExpense.created_at)}</span>
                </div>
                {selectedExpense.status === 'registered' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Estado operacional:</span>
                    <span>Registada sem aprovação manual</span>
                  </div>
                )}
                {selectedExpense.approved_by_name && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Aprovado por:</span>
                    <span>{selectedExpense.approved_by_name} em {formatDateTime(selectedExpense.approved_at)}</span>
                  </div>
                )}
                {selectedExpense.paid_by_name && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pago por:</span>
                    <span>{selectedExpense.paid_by_name} em {formatDateTime(selectedExpense.paid_at)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {selectedExpense && (
            <DialogFooter className="sticky bottom-0 z-20 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-6 sm:py-4">
              <div className="flex w-full flex-wrap justify-between gap-2">
                <Button type="button" variant="outline" className="h-10 rounded-xl px-4" onClick={() => setViewDialogOpen(false)}>
                  Fechar
                </Button>
                <div className="flex flex-wrap justify-end gap-2">
                  {canReceiveInventory && selectedExpense.inventory_sync_status === 'pending_receipt' && (
                    <Button type="button" variant="outline" className="h-10 rounded-xl px-5" onClick={() => handleConfirmInventoryReceipt(selectedExpense)} data-testid="expense-view-confirm-inventory-button">
                      Confirmar recepção / entrada em stock
                    </Button>
                  )}
                  {canReceiveInventory && ['pending_receipt', 'received', 'reversal_blocked'].includes(selectedExpense.inventory_sync_status) && selectedExpense.inventory_linked_items_count > 0 && (
                    <Button type="button" variant="outline" className="h-10 rounded-xl px-5" onClick={() => handleCancelInventorySync(selectedExpense)} data-testid="expense-view-cancel-inventory-button">
                      Anular integração com histórico
                    </Button>
                  )}
                  {selectedExpense.status === 'cancelled' && isAdmin && (
                    <Button type="button" variant="destructive" className="h-10 rounded-xl px-5" onClick={() => handleOpenPermanentDelete(selectedExpense)} data-testid="expense-view-permanent-delete-button">
                      Eliminar permanentemente
                    </Button>
                  )}
                  {['registered', 'approved'].includes(selectedExpense.status) && canApprove && getExpensePaymentBalance(selectedExpense) > 0 && (
                    <Button type="button" variant="outline" className="h-10 rounded-xl px-5" onClick={() => handleOpenPay(selectedExpense)} data-testid="expense-view-register-payment-button">
                      Registar pagamento
                    </Button>
                  )}
                  {canEditExpense(selectedExpense) && (
                    <Button type="button" className="h-10 rounded-xl bg-slate-900 px-5 text-white hover:bg-slate-800" onClick={() => handleEdit(selectedExpense)} data-testid="expense-view-edit-button">
                      Editar Despesa
                    </Button>
                  )}
                </div>
              </div>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={quickCreateDialogOpen} onOpenChange={setQuickCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{quickCreateType === 'supplier' ? 'Novo Fornecedor' : 'Novo Colaborador'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={quickCreateForm.name} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, name: e.target.value })} />
            </div>
            {quickCreateType === 'supplier' ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pessoa de Contacto</Label>
                    <Input value={quickCreateForm.contact_person} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, contact_person: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>NUIT</Label>
                    <Input value={quickCreateForm.tax_id} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, tax_id: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input value={quickCreateForm.phone} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, phone: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input value={quickCreateForm.email} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, email: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Morada</Label>
                  <Input value={quickCreateForm.address} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, address: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cidade</Label>
                    <Input value={quickCreateForm.city} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, city: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Prazo Pagamento</Label>
                    <Select value={quickCreateForm.payment_terms} onValueChange={(value) => setQuickCreateForm({ ...quickCreateForm, payment_terms: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="À vista">À vista</SelectItem>
                        <SelectItem value="15 dias">15 dias</SelectItem>
                        <SelectItem value="30 dias">30 dias</SelectItem>
                        <SelectItem value="45 dias">45 dias</SelectItem>
                        <SelectItem value="60 dias">60 dias</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notas</Label>
                  <Textarea value={quickCreateForm.notes} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, notes: e.target.value })} rows={2} />
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nº Colaborador</Label>
                    <Input value={quickCreateForm.employee_number} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, employee_number: e.target.value })} placeholder="001" />
                  </div>
                  <div className="space-y-2">
                    <Label>Função</Label>
                    <Select value={quickCreateForm.role} onValueChange={(value) => setQuickCreateForm({ ...quickCreateForm, role: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="cashier">Cashier</SelectItem>
                        <SelectItem value="store_manager">Store Manager</SelectItem>
                        <SelectItem value="accountant">Accountant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input value={quickCreateForm.phone} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, phone: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input value={quickCreateForm.email} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, email: e.target.value })} />
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label>Pode vender</Label>
                  <input type="checkbox" checked={quickCreateForm.can_sell} onChange={(e) => setQuickCreateForm({ ...quickCreateForm, can_sell: e.target.checked })} />
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setQuickCreateDialogOpen(false)}>Cancelar</Button>
              <Button type="button" onClick={handleQuickCreateCounterparty}>Guardar e seleccionar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="h-[100dvh] w-screen max-h-[100dvh] max-w-none overflow-hidden rounded-none border-0 bg-slate-100/80 p-0" data-testid="expense-edit-fullscreen-dialog">
          {editFormData && (
            <div className="mx-auto flex h-full min-h-0 w-full max-w-[min(100vw,1980px)] flex-col bg-white shadow-2xl">
              <DialogHeader className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 sm:px-6 sm:py-4 xl:px-8">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      <Edit className="h-3.5 w-3.5" />
                      Edição em ecrã inteiro
                    </div>
                    <DialogTitle className="text-left text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
                      Editar Despesa
                    </DialogTitle>
                    <DialogDescription className="max-w-2xl text-left text-xs text-slate-500 sm:text-sm">
                      Actualização directa dos dados com justificação auditada em baixo.
                    </DialogDescription>
                  </div>
                  <div className="hidden rounded-xl border border-slate-200 bg-white px-3 py-2 text-right sm:block">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Documento</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{selectedExpense?.expense_code}</p>
                  </div>
                </div>
              </DialogHeader>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-6 xl:px-8 2xl:px-10">
                <div className="mx-auto grid max-w-[1840px] gap-4 pb-24 xl:grid-cols-[minmax(620px,1.08fr)_minmax(420px,0.92fr)] xl:gap-5 2xl:grid-cols-[minmax(760px,1.08fr)_minmax(520px,0.92fr)] 2xl:gap-6">
                  <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Dados base</p>
                        <h3 className="mt-1 text-lg font-semibold text-slate-950">Montante e descrição</h3>
                      </div>
                      <div className="rounded-2xl bg-amber-50 px-3 py-2 text-right">
                        <p className="text-[11px] uppercase tracking-wide text-amber-700">Valor</p>
                        <p className="text-lg font-semibold text-amber-700">{editFormData.amount || '0.00'} MTn</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Descrição *</Label>
                        <Input value={editFormData.description} onChange={(e) => setEditFormData({...editFormData, description: e.target.value})} className="h-12 rounded-2xl border-slate-300 bg-white" />
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Valor (MZN) *</Label>
                          <Input type="number" step="0.01" value={editFormData.amount} onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})} disabled={(editFormData.items || []).length > 0} className="h-12 rounded-2xl border-slate-300 bg-white text-lg font-semibold disabled:cursor-not-allowed disabled:bg-slate-100" />
                          {(editFormData.items || []).length > 0 && <p className="text-xs text-muted-foreground" data-testid="edit-expense-amount-derived-note">Valor calculado automaticamente pela soma dos artigos.</p>}
                        </div>
                        <div className="space-y-2">
                          <Label>Data da Despesa *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className={cn("h-12 w-full justify-start rounded-2xl border-slate-300 bg-white text-left font-normal", !editFormData.expense_date && "text-muted-foreground")}>
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {editFormData.expense_date ? format(editFormData.expense_date, "PPP", { locale: pt }) : "Selecionar data"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={editFormData.expense_date} onSelect={(date) => setEditFormData({...editFormData, expense_date: date || new Date()})} initialFocus /></PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2"><Clock className="h-4 w-4 text-amber-500" /> Data/Hora do Registo</Label>
                        <Input type="datetime-local" value={editFormData.registered_at} onChange={(e) => setEditFormData({...editFormData, registered_at: e.target.value})} max={new Date().toISOString().slice(0, 16)} className="h-12 rounded-2xl border-amber-200 bg-white" />
                      </div>
                    </div>
                  </section>

                  <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Classificação</p>
                    <h3 className="mt-1 text-lg font-semibold text-slate-950">Categoria e rastreio</h3>
                    <div className="mt-4 space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Categoria *</Label>
                          <Select value={editFormData.expense_category_id} onValueChange={(v) => {
                            setEditSubcategories([]);
                            setEditFormData({...editFormData, expense_category_id: v, expense_subcategory_id: ''});
                          }} disabled={['received', 'reversal_blocked'].includes(selectedExpense?.inventory_sync_status)}>
                            <SelectTrigger className="h-12 rounded-2xl border-slate-300 bg-white" data-testid="edit-expense-category-select"><SelectValue placeholder="Selecionar categoria" /></SelectTrigger>
                            <SelectContent>{topLevelCategories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.expense_code} · {cat.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Subcategoria {currentEditSubcategories.length > 0 ? '*' : ''}</Label>
                          <Select key={`edit-subcategory-${editFormData.expense_category_id || 'none'}`} value={editFormData.expense_subcategory_id} onValueChange={(v) => setEditFormData({...editFormData, expense_subcategory_id: v === 'none' ? '' : v})} disabled={!editFormData.expense_category_id || ['received', 'reversal_blocked'].includes(selectedExpense?.inventory_sync_status)}>
                            <SelectTrigger data-testid="edit-expense-subcategory-select" className="h-12 rounded-2xl border-slate-300 bg-white"><SelectValue placeholder={currentEditSubcategories.length > 0 ? 'Selecionar subcategoria' : 'Opcional'} /></SelectTrigger>
                            <SelectContent>
                              {currentEditSubcategories.length === 0 && <SelectItem value="none">Nenhuma</SelectItem>}
                              {currentEditSubcategories.map(sub => <SelectItem key={sub.id} value={sub.id}>{sub.expense_code} · {sub.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid gap-4 md:grid-cols-1">
                        <div className="space-y-2">
                          <Label>Localização *</Label>
                          <Select value={editFormData.location_id} onValueChange={(v) => setEditFormData({...editFormData, location_id: v})} disabled={['received', 'reversal_blocked'].includes(selectedExpense?.inventory_sync_status)}>
                            <SelectTrigger className="h-12 rounded-2xl border-slate-300 bg-white" data-testid="edit-expense-location-select"><SelectValue placeholder="Onde foi a despesa?" /></SelectTrigger>
                            <SelectContent>{locations.map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </section>

                  <div className="xl:col-span-2">
                    {['received', 'reversal_blocked'].includes(selectedExpense?.inventory_sync_status) && (
                      <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="expense-edit-inventory-lock-note">
                        Esta despesa já teve entrada em stock confirmada. A edição estrutural foi bloqueada; use anulação controlada se precisar reverter.
                      </div>
                    )}
                    <ExpenseItemsEditor
                      items={editFormData.items || []}
                      onAddItem={addEditExpenseItem}
                      onRemoveItem={removeEditExpenseItem}
                      onItemChange={updateEditExpenseItem}
                      totalAmount={editItemsSummary.total.toFixed(2)}
                      testIdPrefix="expense-edit-items-editor"
                      inventoryEnabled={isEditPgtInventoryEligible}
                      products={inventoryProducts}
                      locations={locations}
                      showInventoryReadOnly={['received', 'reversal_blocked'].includes(selectedExpense?.inventory_sync_status)}
                    />
                  </div>

                  <div className="xl:col-span-2">
                    <ExpenseSplitPaymentsEditor
                      payments={editFormData.payments || []}
                      paymentMethods={paymentMethods}
                      totalAmount={editFormData.amount}
                      onAddPayment={addEditExpensePayment}
                      onRemovePayment={removeEditExpensePayment}
                      onPaymentChange={updateEditExpensePayment}
                      title="Formas de pagamento da despesa"
                      description="Ajuste a divisão por métodos sem voltar a passar por aprovação manual."
                      testIdPrefix="expense-edit-payments"
                    />
                  </div>

                  <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4 xl:col-span-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Pagamento e destinatário</p>
                    <h3 className="mt-1 text-lg font-semibold text-slate-950">Fornecedor ou beneficiário</h3>
                    <div className="mt-4 grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
                      <div className="space-y-2">
                        <Label>Nº Factura/Recibo</Label>
                        <Input value={editFormData.reference_number} onChange={(e) => setEditFormData({...editFormData, reference_number: e.target.value})} className="h-12 rounded-2xl border-slate-300 bg-white" />
                      </div>
                      <div className="space-y-2">
                        <Label>Fornecedor/Beneficiário</Label>
                        <div className="grid gap-2 md:grid-cols-[160px_minmax(0,1fr)_auto]">
                          <Select value={editCounterpartyType} onValueChange={(value) => {
                            setEditCounterpartyError('');
                            setEditFormData({ ...editFormData, counterparty_type: value, supplier_id: '', beneficiary_employee_id: '', supplier_name: '' });
                          }}>
                            <SelectTrigger className="h-12 rounded-2xl border-slate-300 bg-white" data-testid="edit-expense-counterparty-type-select"><SelectValue placeholder="Tipo" /></SelectTrigger>
                            <SelectContent><SelectItem value="supplier">Fornecedor</SelectItem><SelectItem value="employee">Colaborador</SelectItem></SelectContent>
                          </Select>
                          {editCounterpartyType === 'supplier' ? (
                            <Select value={editFormData.supplier_id || ''} onValueChange={(value) => {
                              setEditCounterpartyError('');
                              setEditFormData({ ...editFormData, supplier_id: value, beneficiary_employee_id: '', supplier_name: editSupplierOptions.find(item => item.id === value)?.name || '' });
                            }}>
                              <SelectTrigger className="h-12 rounded-2xl border-slate-300 bg-white" data-testid="edit-expense-supplier-select"><SelectValue placeholder="Seleccionar fornecedor" /></SelectTrigger>
                              <SelectContent>{editSupplierOptions.map(item => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (
                            <Select value={editFormData.beneficiary_employee_id || ''} onValueChange={(value) => {
                              setEditCounterpartyError('');
                              setEditFormData({ ...editFormData, beneficiary_employee_id: value, supplier_id: '', supplier_name: editBeneficiaryOptions.find(item => item.id === value)?.name || '' });
                            }}>
                              <SelectTrigger className="h-12 rounded-2xl border-slate-300 bg-white" data-testid="edit-expense-beneficiary-select"><SelectValue placeholder="Seleccionar colaborador" /></SelectTrigger>
                              <SelectContent>{editBeneficiaryOptions.map(item => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                            </Select>
                          )}
                          <Button type="button" variant="outline" className="h-12 rounded-2xl" onClick={() => {
                            setQuickCreateTarget('edit');
                            setQuickCreateType(editFormData.counterparty_type || 'supplier');
                            setQuickCreateForm({ name: '', phone: '', email: '', employee_number: '', role: 'staff', can_sell: true, contact_person: '', tax_id: '', address: '', city: 'Maputo', payment_terms: '30 dias', notes: '' });
                            setQuickCreateDialogOpen(true);
                          }}>
                            {(editFormData.counterparty_type || 'supplier') === 'supplier' ? 'Novo fornecedor' : 'Novo colaborador'}
                          </Button>
                        </div>
                        {editCounterpartyError && (
                          <p className="text-sm font-medium text-rose-600" data-testid="edit-expense-counterparty-validation-message">
                            {editCounterpartyError}
                          </p>
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="rounded-[24px] border border-slate-200 bg-slate-50/70 p-4 sm:p-5 xl:col-span-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Contexto e auditoria</p>
                    <h3 className="mt-1 text-lg font-semibold text-slate-950">Notas operacionais</h3>
                    <div className="mt-4 grid gap-4 xl:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Notas</Label>
                        <Textarea value={editFormData.notes} onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})} rows={4} className="min-h-[140px] rounded-2xl border-slate-300 bg-white" />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-medium text-amber-700">Justificação da Alteração *</Label>
                        <Textarea value={editJustification} onChange={(e) => setEditJustification(e.target.value)} placeholder="Descreva o motivo desta alteração..." rows={4} className="min-h-[140px] rounded-2xl border-amber-300 bg-white" />
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              <div className="sticky bottom-0 z-20 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-6 sm:py-4 xl:px-8">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <strong>Edição auditada:</strong> todas as alterações serão registadas para esta despesa.
                    <div className="mt-2 text-xs text-amber-700" data-testid="expense-edit-payments-preview">
                      Pagamentos actuais: {formatCurrency(editPaymentsSummary.total)} · Saldo previsto: {formatCurrency(Math.max(Number(editFormData.amount || 0) - editPaymentsSummary.total, 0))}
                    </div>
                  </div>
                  <div className="flex gap-2 sm:flex-shrink-0">
                    <Button variant="outline" className="h-12 rounded-2xl px-5" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveEdit} className="h-12 rounded-2xl bg-slate-900 px-6 text-white hover:bg-slate-800" data-testid="edit-expense-save-button">Guardar Alterações</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="max-h-[96vh] w-[min(100vw-1rem,1680px)] max-w-[1680px] overflow-hidden rounded-[28px] border border-slate-200 bg-white p-0 shadow-2xl" data-testid="expense-pay-dialog-shell">
          <div className="flex flex-col">
            <DialogHeader className="border-b border-slate-200 bg-slate-50/80 px-6 py-5">
              <DialogTitle className="flex items-center gap-2 text-xl font-semibold text-slate-950">
                <CreditCard className="h-5 w-5 text-green-600" />
                Registar Pagamento
              </DialogTitle>
              <p className="text-sm text-slate-600" data-testid="expense-pay-dialog-description">
                Registe um ou vários pagamentos para a despesa {selectedExpense?.expense_code} sem perder contexto operacional.
              </p>
            </DialogHeader>

            <div className="grid gap-0 xl:grid-cols-[360px_minmax(0,1fr)] 2xl:grid-cols-[400px_minmax(0,1fr)]">
              <aside className="border-b border-slate-200 bg-slate-50/70 p-6 lg:border-b-0 lg:border-r" data-testid="expense-pay-dialog-summary-panel">
                <div className="space-y-3">
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <p className="text-xs text-slate-500">Total da despesa</p>
                    <p className="mt-1 text-lg font-semibold text-slate-950" data-testid="expense-pay-total-amount">{formatCurrency(selectedExpense?.amount || 0)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <p className="text-xs text-slate-500">Já pago</p>
                    <p className="mt-1 text-lg font-semibold text-emerald-700" data-testid="expense-pay-already-paid">{formatCurrency(selectedExpense?.payments_total || 0)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <p className="text-xs text-slate-500">Saldo</p>
                    <p className="mt-1 text-lg font-semibold text-amber-700" data-testid="expense-pay-balance">{formatCurrency(getExpensePaymentBalance(selectedExpense))}</p>
                  </div>
                  <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800" data-testid="expense-pay-dialog-guidance">
                    <p className="font-medium">Janela afinada</p>
                    <p className="mt-1 text-xs">Agora o resumo fica separado do editor para melhorar leitura, alinhamento e controlo do saldo.</p>
                  </div>
                </div>
              </aside>

              <div className="max-h-[74vh] overflow-y-auto px-6 py-5 xl:px-7 2xl:px-8" data-testid="expense-pay-dialog-form-panel">
                <div className="space-y-5">
                  <ExpenseSplitPaymentsEditor
                    payments={paymentData.payments || []}
                    paymentMethods={paymentMethods}
                    totalAmount={getExpensePaymentBalance(selectedExpense)}
                    onAddPayment={addDialogExpensePayment}
                    onRemovePayment={removeDialogExpensePayment}
                    onPaymentChange={updateDialogExpensePayment}
                    title="Pagamento em parcelas"
                    description="Use uma ou várias formas de pagamento até fechar o saldo da despesa."
                    testIdPrefix="expense-pay-dialog"
                  />

                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <Label>Notas</Label>
                    <Textarea
                      value={paymentData.notes}
                      onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                      placeholder="Observações sobre o pagamento"
                      rows={4}
                      className="mt-2 min-h-[140px] rounded-2xl border-slate-300 bg-slate-50"
                      data-testid="expense-pay-dialog-notes-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500" data-testid="expense-pay-dialog-footer-text">
                Total preparado: {formatCurrency(paymentDialogSummary.total)} · Saldo após esta operação: {formatCurrency(Math.max(getExpensePaymentBalance(selectedExpense) - paymentDialogSummary.total, 0))}
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => setPayDialogOpen(false)} data-testid="expense-pay-dialog-cancel-button">
                  Cancelar
                </Button>
                <Button type="button" className="h-10 rounded-xl bg-green-600 hover:bg-green-700" onClick={handlePaySubmit} data-testid="expense-pay-dialog-confirm-button">
                  {paymentDialogSummary.total >= getExpensePaymentBalance(selectedExpense) && getExpensePaymentBalance(selectedExpense) > 0 ? 'Liquidar Despesa' : 'Registar Pagamento'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete/Cancel Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Cancelar Despesa
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>Tem certeza que deseja mover a despesa {selectedExpense?.expense_code} para a tab <strong>Canceladas</strong>?</p>
              
              <div className="space-y-2">
                <Label className="text-red-700">Motivo do Cancelamento *</Label>
                <Textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Descreva o motivo do cancelamento..."
                  rows={2}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSubmit}
              className="bg-red-600 hover:bg-red-700"
              disabled={!deleteReason || deleteReason.trim().length < 5}
            >
              Cancelar Despesa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={purgeDialogOpen} onOpenChange={setPurgeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Eliminação Permanente
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Vai eliminar definitivamente a despesa cancelada <strong>{selectedExpense?.expense_code}</strong>. Esta acção não pode ser desfeita.
              </p>
              <div className="space-y-2">
                <Label className="text-red-700">Motivo da eliminação *</Label>
                <Textarea
                  value={purgeReason}
                  onChange={(e) => setPurgeReason(e.target.value)}
                  placeholder="Explique porque esta despesa pode ser removida em definitivo..."
                  rows={3}
                  data-testid="expense-permanent-delete-reason-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePermanentDeleteSubmit}
              className="bg-red-700 hover:bg-red-800"
              disabled={!purgeReason || purgeReason.trim().length < 5}
              data-testid="expense-permanent-delete-confirm-button"
            >
              Eliminar Permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={renumberDialogOpen} onOpenChange={setRenumberDialogOpen}>
        <AlertDialogContent data-testid="expenses-renumber-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-indigo-600" />
              Executar renumeração sequencial
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Esta acção vai reorganizar os códigos das despesas para <strong>PGT00001</strong>, <strong>PGT00002</strong>, ... a partir da sequência actual do live.
              </p>
              <p>
                O backend também actualiza referências ligadas em histórico e anexos. Recomenda-se correr primeiro a <strong>prévia</strong> e usar esta acção apenas no live real.
              </p>
              {renumberResult && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="expenses-renumber-confirm-summary">
                  Última prévia: {renumberResult.changed_expenses || 0} despesas serão ajustadas.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRunRenumberCodes}
              className="bg-indigo-900 hover:bg-indigo-800"
              data-testid="expenses-renumber-confirm-button"
            >
              Confirmar renumeração
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-600" />
              Histórico - {selectedExpense?.expense_code}
            </DialogTitle>
          </DialogHeader>
          {auditLogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhuma alteração registada</p>
          ) : (
            <div className="space-y-3">
              {auditLogs.map((log, index) => (
                <div key={log.mongo_id || log.id || `${log.expense_id || 'expense'}-${log.action || 'action'}-${index}`} className="rounded-lg border bg-white p-3 shadow-sm" data-testid={`expense-audit-log-${log.id || index}`}>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant={
                      log.action === 'created' ? 'default' :
                      log.action === 'registered' ? 'outline' :
                      log.action === 'approved' ? 'outline' :
                      log.action === 'payment_registered' ? 'outline' :
                      log.action === 'paid' ? 'secondary' :
                      log.action === 'rejected' ? 'destructive' : 'secondary'
                    }>
                      {log.action === 'created' && 'Criada'}
                      {log.action === 'updated' && 'Actualizada'}
                      {log.action === 'registered' && 'Registada'}
                      {log.action === 'submitted' && 'Submetida'}
                      {log.action === 'approved' && 'Aprovada'}
                      {log.action === 'payment_registered' && 'Pagamento registado'}
                      {log.action === 'rejected' && 'Rejeitada'}
                      {log.action === 'paid' && 'Paga'}
                      {log.action === 'cancelled' && 'Cancelada'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(log.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-slate-400" />
                    <span>{log.user_name}</span>
                    <Badge variant="outline" className="text-xs">{log.user_role}</Badge>
                  </div>
                  {log.justification && (
                    <p className="mt-2 text-sm italic text-slate-600" data-testid={`expense-audit-log-justification-${log.id || index}`}>
                      &ldquo;{log.justification}&rdquo;
                    </p>
                  )}
                  {log.notes && (
                    <p className="mt-2 text-xs text-amber-700" data-testid={`expense-audit-log-notes-${log.id || index}`}>
                      {log.notes}
                    </p>
                  )}
                  {Array.isArray(log.changes) && log.changes.length > 0 && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`expense-audit-log-changes-${log.id || index}`}>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">O que mudou</p>
                      <div className="space-y-2">
                        {log.changes.map((change, changeIndex) => {
                          const fieldLabel = EXPENSE_AUDIT_FIELD_LABELS[change.field] || change.field || 'Campo';
                          return (
                            <div key={`${log.id || index}-${change.field || 'field'}-${changeIndex}`} className="rounded-md bg-white p-2 shadow-sm" data-testid={`expense-audit-log-change-${log.id || index}-${changeIndex}`}>
                              <p className="text-xs font-semibold text-slate-700">{fieldLabel}</p>
                              <div className="mt-1 grid gap-1 text-xs text-slate-600 md:grid-cols-2">
                                <p><span className="font-medium text-slate-500">Antes:</span> {formatExpenseAuditChangeValue(change.field, change.from)}</p>
                                <p><span className="font-medium text-slate-500">Depois:</span> {formatExpenseAuditChangeValue(change.field, change.to)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
